import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent } from 'react';
import DrawingCanvas, { type DrawingCanvasHandle } from './components/DrawingCanvas';
import Countdown from './components/Countdown';
import BlindDrawingRouter from './BlindDrawing';
import BlindMarriageRouter from './BlindMarriage';
import { disconnectActiveSocket, selectGameSocket, socket, type GameMode } from './socket';
import { createAlbumGifBlob, exportAlbumGif } from './gifExporter';
import { playPresenceSound } from './presenceSounds';
import type {
  AckResponse,
  BlindState,
  BlindTask,
  MarriageState,
  MarriageTask,
  MarriageGender,
  GameChain,
  GameTask,
  GameSettings,
  PartyRoomState,
  Player,
  RoomState,
  SketchGameTask,
  SketchRevealChainPayload,
  StoryRevealPayload,
  StoryState,
  StoryTask,
  WhoSaidResultsPayload,
  WhoSaidState,
  WhoSaidTask
} from './types';

const ADMIN_TOKEN_KEY = 'mbb-party.admin.session';

function tokenKey(mode: GameMode) { return `mbb-party.${mode}.session`; }
function nameKey(mode: GameMode) { return `mbb-party.${mode}.nickname`; }

function cookieStorageKey(key: string) { return key.replace(/[^A-Za-z0-9_-]/g, '_'); }
function readPersistentValue(key: string): string | null {
  const local = localStorage.getItem(key);
  if (local) return local;
  const cookieName = `${cookieStorageKey(key)}=`;
  const cookie = document.cookie.split(';').map((part) => part.trim()).find((part) => part.startsWith(cookieName));
  if (!cookie) return null;
  try { return decodeURIComponent(cookie.slice(cookieName.length)); } catch { return cookie.slice(cookieName.length); }
}
function writePersistentValue(key: string, value: string): void {
  localStorage.setItem(key, value);
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${cookieStorageKey(key)}=${encodeURIComponent(value)}; Path=/; Max-Age=${60 * 60 * 24 * 30}; SameSite=Lax${secure}`;
}
function removePersistentValue(key: string): void {
  localStorage.removeItem(key);
  document.cookie = `${cookieStorageKey(key)}=; Path=/; Max-Age=0; SameSite=Lax`;
}

function modeFromUrl(): GameMode | null {
  const value = new URLSearchParams(window.location.search).get('game');
  if (value === 'who-said') return 'who-said';
  if (value === 'story' || value === 'complete-story') return 'story';
  if (value === 'blind' || value === 'blind-drawing' || value === 'blinddrawing') return 'blind';
  if (value === 'marriage' || value === 'blind-marriage' || value === 'blindmarriage') return 'marriage';
  if (value === 'sketch' || value === 'sketchrelay') return 'sketch';
  return null;
}

interface SharedArchivePresentation {
  id: string;
  title: string;
  gameType: 'sketch';
  startedAt: number;
  sharedAt: number;
  gifUrl: string;
}

function currentSketchTask(task: GameTask | null, room: RoomState): SketchGameTask | null {
  if (!task || (task.kind !== 'text' && task.kind !== 'drawing')) return null;
  if (room.phase !== 'playing' || !room.sessionId) return null;
  if (task.gameId !== room.sessionId) return null;
  if (task.turn !== room.turn || task.kind !== room.turnKind) return null;
  if (task.endsAt !== room.endsAt) return null;
  return task;
}

export default function App() {
  return window.location.pathname.startsWith('/admin') ? <AdminDashboard /> : <GameApp />;
}

function GameApp() {
  const [selectedMode, setSelectedMode] = useState<GameMode | null>(() => modeFromUrl());
  const [self, setSelf] = useState<Player | null>(null);
  const [room, setRoom] = useState<PartyRoomState | null>(null);
  const [task, setTask] = useState<GameTask | null>(null);
  const [chains, setChains] = useState<GameChain[]>([]);
  const [whoResults, setWhoResults] = useState<WhoSaidResultsPayload | null>(null);
  const [storyReveal, setStoryReveal] = useState<StoryRevealPayload | null>(null);
  const [connected, setConnected] = useState(false);
  const [ready, setReady] = useState(true);
  const [toast, setToast] = useState<string>('');
  const [startCountdownEndsAt, setStartCountdownEndsAt] = useState<number | null>(null);
  const [chefArchiveOpen, setChefArchiveOpen] = useState(false);
  const [sharedArchive, setSharedArchive] = useState<SharedArchivePresentation | null>(null);
  const [serverClockOffsetMs, setServerClockOffsetMs] = useState(0);
  const roomRef = useRef<PartyRoomState | null>(null);
  const chainsRef = useRef<GameChain[]>([]);
  const bestClockRttRef = useRef(Number.POSITIVE_INFINITY);
  const hasPreciseClockRef = useRef(false);
  const readyChainIdsRef = useRef(new Set<string>());
  const preloadingChainIdsRef = useRef(new Set<string>());

  const observeServerClock = useCallback((serverTime: unknown, requestStartedAt?: number, requestEndedAt?: number) => {
    const numeric = Number(serverTime);
    if (!Number.isFinite(numeric)) return;
    const endedAt = requestEndedAt ?? Date.now();
    if (requestStartedAt !== undefined) {
      const rtt = Math.max(0, endedAt - requestStartedAt);
      if (rtt <= bestClockRttRef.current + 20) {
        bestClockRttRef.current = Math.min(bestClockRttRef.current, rtt);
        hasPreciseClockRef.current = true;
        setServerClockOffsetMs(numeric - ((requestStartedAt + endedAt) / 2));
      }
      return;
    }
    if (!hasPreciseClockRef.current) setServerClockOffsetMs(numeric - endedAt);
  }, []);

  const acceptRoomState = useCallback((state: PartyRoomState): boolean => {
    if (state.mode === 'sketch') {
      observeServerClock(state.serverTime);
      const current = roomRef.current;
      if (
        current?.mode === 'sketch' &&
        current.phase === 'reveal' &&
        state.phase === 'reveal' &&
        current.sessionId === state.sessionId &&
        current.album && state.album &&
        state.album.revision < current.album.revision
      ) {
        return false;
      }
    }
    roomRef.current = state;
    setRoom(state);
    return true;
  }, [observeServerClock]);

  const ingestSketchReveal = useCallback((payload?: SketchRevealChainPayload) => {
    const index = Number(payload?.index);
    const total = Number(payload?.total);
    if (!payload || !Number.isInteger(index) || index < 0 || !payload.chain || !payload.gameId) return;
    const current = roomRef.current;
    if (current?.mode === 'sketch' && current.sessionId && current.sessionId !== payload.gameId) return;

    setChains((existing) => {
      const next = existing.slice();
      const targetLength = Number.isInteger(total) && total > 0 ? Math.max(total, index + 1) : Math.max(next.length, index + 1);
      next.length = targetLength;
      next[index] = payload.chain;
      chainsRef.current = next;
      return next;
    });

    const readyKey = `${payload.gameId}:${index}:${payload.chain.id}`;
    const finishReady = () => {
      const latest = roomRef.current;
      if (latest?.mode !== 'sketch' || latest.sessionId !== payload.gameId) return;
      const startedAt = Date.now();
      socket.emit('game:albumReady', {
        gameId: payload.gameId,
        chainIndex: index,
        chainId: payload.chain.id,
        revision: payload.revision
      }, (response: AckResponse) => observeServerClock(response?.serverTime, startedAt, Date.now()));
    };

    if (readyChainIdsRef.current.has(readyKey)) {
      finishReady();
      return;
    }
    if (preloadingChainIdsRef.current.has(readyKey)) return;

    preloadingChainIdsRef.current.add(readyKey);
    void preloadSketchChain(payload.chain).finally(() => {
      preloadingChainIdsRef.current.delete(readyKey);
      readyChainIdsRef.current.add(readyKey);
      finishReady();
    });
  }, [observeServerClock]);

  const handleJoinAck = useCallback((response: AckResponse, fallbackNickname: string, mode: GameMode) => {
    if (!response.ok || !response.self || !response.state || !response.token) {
      throw new Error(response.error || 'تعذر الدخول إلى اللعبة.');
    }
    writePersistentValue(tokenKey(mode), response.token);
    writePersistentValue(nameKey(mode), response.self.nickname || fallbackNickname);
    setSelf(response.self);
    roomRef.current = response.state;
    if (response.state.mode === 'sketch') observeServerClock(response.state.serverTime);
    setRoom(response.state);
    setReady(true);
  }, [observeServerClock]);

  useEffect(() => {
    if (!selectedMode) {
      disconnectActiveSocket();
      setConnected(false);
      setReady(true);
      setStartCountdownEndsAt(null);
      setSharedArchive(null);
      return;
    }

    setReady(false);
    const active = selectGameSocket(selectedMode);
    const mode = selectedMode;

    const resume = () => {
      const token = readPersistentValue(tokenKey(mode));
      const nickname = readPersistentValue(nameKey(mode)) || 'Player';
      if (!token) {
        setReady(true);
        return;
      }
      active.emit('player:join', { nickname, sessionToken: token }, (response: AckResponse) => {
        try {
          handleJoinAck(response, nickname, mode);
        } catch {
          removePersistentValue(tokenKey(mode));
          removePersistentValue(nameKey(mode));
          setSelf(null);
          setRoom(null);
          setTask(null);
          setWhoResults(null);
          setStoryReveal(null);
          setReady(true);
        }
      });
    };

    const onConnect = () => {
      setConnected(true);
      resume();
    };
    const onDisconnect = () => setConnected(false);
    const onState = (state: PartyRoomState) => {
      if (!acceptRoomState(state)) return;
      const lobbyPhase = state.mode === 'who-said'
        ? state.phase === 'who_lobby'
        : state.mode === 'story'
          ? state.phase === 'story_lobby'
          : state.mode === 'blind'
            ? state.phase === 'blind_lobby'
            : state.mode === 'marriage'
              ? state.phase === 'marriage_lobby'
              : state.phase === 'lobby';
      if (!lobbyPhase) setStartCountdownEndsAt(null);
    };
    const onStartCountdown = (payload: { endsAt?: number } | null) => {
      const endsAt = Number(payload?.endsAt);
      if (Number.isFinite(endsAt) && endsAt > Date.now()) setStartCountdownEndsAt(endsAt);
    };
    const onStartCountdownCancelled = () => setStartCountdownEndsAt(null);
    const onTask = (value: GameTask | null) => setTask(value);
    const onReveal = (value: GameChain[]) => {
      chainsRef.current = value;
      setChains(value);
    };
    const onRevealChain = (payload?: SketchRevealChainPayload) => ingestSketchReveal(payload);
    const onWhoResults = (value: WhoSaidResultsPayload) => setWhoResults(value);
    const onStoryReveal = (value: StoryRevealPayload) => setStoryReveal(value);
    const onArchiveShow = (value: SharedArchivePresentation) => setSharedArchive(value);
    const onArchiveHide = (value?: { id?: string }) => setSharedArchive((current) => (!value?.id || current?.id === value.id ? null : current));
    const onPresence = (value?: { action?: 'join' | 'leave' }) => {
      if (value?.action === 'join') playPresenceSound('join');
      if (value?.action === 'leave') playPresenceSound('leave');
    };
    const onAlbumActivated = (value?: { serverTime?: number }) => observeServerClock(value?.serverTime);
    const onKicked = () => {
      removePersistentValue(tokenKey(mode));
      removePersistentValue(nameKey(mode));
      setSelf(null);
      setRoom(null);
      setTask(null);
      setChains([]);
      setWhoResults(null);
      setStoryReveal(null);
      setToast('تم إخراجك من الغرفة بواسطة الأدمن.');
    };
    const onSessionReplaced = () => {
      // Another tab/device resumed this exact player token. Stop this socket
      // permanently so it cannot auto-reconnect and steal the session back.
      setConnected(false);
      setSelf(null);
      setRoom(null);
      setTask(null);
      setChains([]);
      setWhoResults(null);
      setStoryReveal(null);
      setReady(true);
      setToast('تم فتح نفس اللاعب في جهاز أو تبويب آخر. هذه الصفحة توقفت لمنع التحكم المزدوج.');
      active.disconnect();
    };
    const onViewerSessionEnded = () => {
      removePersistentValue(tokenKey(mode));
      removePersistentValue(nameKey(mode));
      setSelf(null);
      setTask(null);
      setChains([]);
      setWhoResults(null);
      setStoryReveal(null);
      setToast('انتهت جلسة المشاهدة. يمكنك الدخول من جديد عند بدء الجولة التالية.');
    };

    active.on('connect', onConnect);
    active.on('disconnect', onDisconnect);
    active.on('room:state', onState);
    active.on('game:startCountdown', onStartCountdown);
    active.on('game:startCountdownCancelled', onStartCountdownCancelled);
    active.on('game:task', onTask);
    active.on('game:reveal', onReveal);
    active.on('game:revealChain', onRevealChain);
    active.on('game:albumActivated', onAlbumActivated);
    active.on('who:results', onWhoResults);
    active.on('story:reveal', onStoryReveal);
    active.on('archive:show', onArchiveShow);
    active.on('archive:hide', onArchiveHide);
    active.on('player:presence', onPresence);
    active.on('player:kicked', onKicked);
    active.on('session:replaced', onSessionReplaced);
    active.on('viewer:sessionEnded', onViewerSessionEnded);

    if (active.connected) {
      setConnected(true);
      resume();
    } else {
      active.connect();
    }

    return () => {
      active.off('connect', onConnect);
      active.off('disconnect', onDisconnect);
      active.off('room:state', onState);
      active.off('game:startCountdown', onStartCountdown);
      active.off('game:startCountdownCancelled', onStartCountdownCancelled);
      active.off('game:task', onTask);
      active.off('game:reveal', onReveal);
      active.off('game:revealChain', onRevealChain);
      active.off('game:albumActivated', onAlbumActivated);
      active.off('who:results', onWhoResults);
      active.off('story:reveal', onStoryReveal);
      active.off('archive:show', onArchiveShow);
      active.off('archive:hide', onArchiveHide);
      active.off('player:presence', onPresence);
      active.off('player:kicked', onKicked);
      active.off('session:replaced', onSessionReplaced);
      active.off('viewer:sessionEnded', onViewerSessionEnded);
    };
  }, [selectedMode, handleJoinAck, acceptRoomState, ingestSketchReveal, observeServerClock]);

  useEffect(() => { chainsRef.current = chains; }, [chains]);
  useEffect(() => { roomRef.current = room; }, [room]);

  useEffect(() => {
    if (!room) return;
    if (room.mode === 'story') {
      if (room.phase === 'story_lobby') {
        setTask(null);
        setStoryReveal(null);
      }
      if (room.phase === 'story_waiting_reveal') setTask(null);
      return;
    }
    if (room.mode === 'blind') {
      if (room.phase === 'blind_lobby' || room.phase === 'blind_reveal' || room.phase === 'blind_results') setTask(null);
      return;
    }
    if (room.mode === 'marriage') {
      if (room.phase === 'marriage_lobby' || room.phase === 'marriage_revealing' || room.phase === 'marriage_elimination_reveal' || room.phase === 'marriage_finale') setTask(null);
      return;
    }
    if (room.mode === 'who-said') {
      if (room.phase === 'who_lobby') {
        setTask(null);
        setWhoResults(null);
      }
      if (room.phase === 'who_ready') setTask(null);
      return;
    }
    if (room.phase === 'lobby' || room.phase === 'album_waiting') {
      setTask(null);
      setChains([]);
      chainsRef.current = [];
      if (room.phase === 'lobby') {
        readyChainIdsRef.current.clear();
        preloadingChainIdsRef.current.clear();
      }
    }
  }, [room?.phase, room?.mode]);

  useEffect(() => {
    if (selectedMode !== 'sketch' || !connected || !self || !room || room.mode !== 'sketch') return;
    let cancelled = false;
    let inFlight = false;
    let timer: number | null = null;

    const schedule = (delay: number) => {
      if (cancelled) return;
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(runSync, delay);
    };

    const runSync = async () => {
      if (cancelled || inFlight || !socket.connected) {
        schedule(800);
        return;
      }
      const current = roomRef.current;
      if (!current || current.mode !== 'sketch') return;
      const isParticipant = Boolean(current.participantPlayerIds?.includes(self.id));
      const isSubmitted = Boolean(current.submittedPlayerIds?.includes(self.id));
      const hasCurrentTask = Boolean(currentSketchTask(task, current));
      const needsTaskSync = current.phase === 'playing' && isParticipant && !isSubmitted && !hasCurrentTask;
      const needsAlbumWatch = current.phase === 'reveal';
      if (!needsTaskSync && !needsAlbumWatch) return;

      const revealIndex = current.album?.chainIndex;
      const knownChain = Number.isInteger(revealIndex) ? chainsRef.current[revealIndex as number] : undefined;
      inFlight = true;
      const startedAt = Date.now();
      try {
        const response = await emitAck('game:sync', {
          knownGameId: current.sessionId,
          knownChainIndex: Number.isInteger(revealIndex) ? revealIndex : -1,
          knownChainId: knownChain?.id || ''
        });
        const endedAt = Date.now();
        if (cancelled || !response.ok) return;
        observeServerClock(response.serverTime, startedAt, endedAt);
        if (response.state) acceptRoomState(response.state);
        if ('task' in response) setTask(response.task ?? null);
        if (response.sketchReveal?.length) {
          setChains(response.sketchReveal);
          chainsRef.current = response.sketchReveal;
        }
        if (response.sketchRevealChain) ingestSketchReveal(response.sketchRevealChain);
      } catch {
        // A temporary mobile-network delay is corrected on the next watchdog pass.
      } finally {
        inFlight = false;
        const latest = roomRef.current;
        if (!cancelled && latest?.mode === 'sketch' && latest.phase === 'reveal') schedule(1000);
        else if (!cancelled && needsTaskSync) schedule(900);
      }
    };

    schedule(room.phase === 'reveal' ? 250 : 280);
    return () => {
      cancelled = true;
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [selectedMode, connected, self?.id, room?.mode, room?.phase, task, acceptRoomState, ingestSketchReveal, observeServerClock]);

  const notify = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast((current) => (current === message ? '' : current)), 3200);
  };

  const chooseMode = (mode: GameMode) => {
    setSelf(null);
    setRoom(null);
    setTask(null);
    setChains([]);
    setWhoResults(null);
    setStoryReveal(null);
    setStartCountdownEndsAt(null);
    setChefArchiveOpen(false);
    setSharedArchive(null);
    setSelectedMode(mode);
    const url = new URL(window.location.href);
    url.searchParams.set('game', mode === 'sketch' ? 'sketchrelay' : mode === 'who-said' ? 'who-said' : mode === 'story' ? 'story' : mode === 'blind' ? 'blind-drawing' : 'blind-marriage');
    window.history.pushState({}, '', url);
  };

  const backHome = () => {
    if (self) return;
    disconnectActiveSocket();
    setSelectedMode(null);
    setRoom(null);
    setTask(null);
    setWhoResults(null);
    setStoryReveal(null);
    setStartCountdownEndsAt(null);
    setChefArchiveOpen(false);
    setSharedArchive(null);
    setConnected(false);
    const url = new URL(window.location.href);
    url.searchParams.delete('game');
    window.history.pushState({}, '', url.pathname + url.search + url.hash);
  };

  const join = async (nickname: string, chefPassword: string, options?: { gender?: MarriageGender; spectator?: boolean }) => {
    if (!selectedMode) return;
    const response = await emitAck('player:join', { nickname, chefPassword, ...(selectedMode === 'marriage' ? { gender: options?.gender ?? 'male', spectator: Boolean(options?.spectator) } : {}) });
    handleJoinAck(response, nickname, selectedMode);
  };

  const logout = async () => {
    if (!self || !selectedMode) return;
    const activePhase = room?.phase;
    const warning = activePhase && !['lobby', 'who_lobby', 'who_results', 'story_lobby', 'story_reveal', 'blind_lobby', 'blind_results', 'marriage_lobby', 'marriage_finale', 'reveal'].includes(activePhase)
      ? 'هل تريد الخروج من اللعبة؟ يمكنك الدخول مجددًا بنفس الاسم والجلسة إذا بقيت الجولة نشطة.'
      : 'هل تريد الخروج من الغرفة؟';
    if (!window.confirm(warning)) return;

    try {
      if (socket.connected) {
        const response = await emitAck('player:leave', {});
        if (!response.ok) throw new Error(response.error || 'تعذر الخروج من الغرفة.');
      }
    } catch (error) {
      notify(messageOf(error));
    } finally {
      removePersistentValue(tokenKey(selectedMode));
      removePersistentValue(nameKey(selectedMode));
      setSelf(null);
      setRoom(null);
      setTask(null);
      setChains([]);
      setWhoResults(null);
      setStoryReveal(null);
      setStartCountdownEndsAt(null);
      setChefArchiveOpen(false);
      setSharedArchive(null);
      setReady(true);
    }
  };

  if (!selectedMode) {
    return (
      <div className="app-shell">
        <AmbientBackground />
        <PartyHome onChoose={chooseMode} />
      </div>
    );
  }

  if (!ready) return <Splash />;

  const isWho = room?.mode === 'who-said' || selectedMode === 'who-said';
  const isStory = room?.mode === 'story' || selectedMode === 'story';
  const isBlind = room?.mode === 'blind' || selectedMode === 'blind';
  const isMarriage = room?.mode === 'marriage' || selectedMode === 'marriage';
  const brandTitle = isWho ? 'من قال هذا؟' : isStory ? 'أكمل القصة' : isBlind ? 'ارسم وأنت أعمى' : isMarriage ? 'الزواج الأعمى' : 'SketchRelay';
  const brandSubtitle = isWho ? 'خمن صاحب الإجابة!' : isStory ? 'اكتب سطرًا... ولا تعرف ماذا حدث قبله!' : isBlind ? 'ارسم بثقة… لأنك لن ترى شيئًا!' : isMarriage ? 'اختيار سري… حتى آخر إجابة!' : 'ارسمها... وخلّيها تضيع!';

  return (
    <div className={`app-shell ${isWho ? 'who-said-theme' : isStory ? 'story-theme' : isBlind ? 'blind-theme' : isMarriage ? 'marriage-theme' : ''}`}>
      <AmbientBackground />
      <header className="topbar">
        <div className="brand-mini">
          <span className="brand-mark company-logo-mark">
            <img src="/brand/mbb-logo-mark.png" alt="MBB Websolutions" />
          </span>
          <div>
            <strong>{brandTitle}</strong>
            <small>{brandSubtitle}</small>
          </div>
        </div>
        <div className="topbar-status">
          <span className={`connection-pill ${connected ? 'online' : 'offline'}`}><i /> {connected ? 'متصل' : 'إعادة الاتصال'}</span>
          {self ? (
            <>
              <span className="user-pill">{self.isChef ? '👑 ' : ''}{self.nickname}</span>
              {self.isChef && <button type="button" className="ghost-btn archive-top-btn" onClick={() => setChefArchiveOpen(true)}>🗂️ <span>الأرشيف</span></button>}
              <button type="button" className="ghost-btn top-logout" onClick={logout} title="الخروج من اللعبة">خروج ↪</button>
            </>
          ) : (
            <button type="button" className="ghost-btn top-logout" onClick={backHome}>كل الألعاب</button>
          )}
        </div>
      </header>

      {self && room && !(room.mode === 'marriage' && !self.isChef && room.phase !== 'marriage_finale') && <PlayerStrip room={room} self={self} notify={notify} />}

      <main className="page-wrap">
        {self?.isChef && room && room.mode === 'sketch' && room.phase !== 'lobby' && (
          <ChefLiveControls room={room} notify={notify} />
        )}
        {self?.isChef && room?.mode === 'who-said' && !['who_lobby', 'who_results'].includes(room.phase) && (
          <WhoChefLiveControls room={room} notify={notify} />
        )}
        {self?.isChef && room?.mode === 'story' && !['story_lobby', 'story_reveal'].includes(room.phase) && (
          <StoryChefLiveControls room={room} notify={notify} />
        )}

        {!self ? (
          <JoinScreen connected={connected} mode={selectedMode} phase={room?.phase} onJoin={join} />
        ) : room?.mode === 'marriage' ? (
          <BlindMarriageRouter self={self} room={room as MarriageState} task={task as MarriageTask | null} notify={notify} />
        ) : room?.mode === 'blind' ? (
          <BlindDrawingRouter self={self} room={room as BlindState} task={task as BlindTask | null} notify={notify} />
        ) : room?.mode === 'story' ? (
          <StoryRouter self={self} room={room} task={task as StoryTask | null} reveal={storyReveal} notify={notify} />
        ) : room?.mode === 'who-said' ? (
          <WhoSaidRouter self={self} room={room} task={task as WhoSaidTask | null} results={whoResults} notify={notify} />
        ) : room?.phase === 'lobby' ? (
          <Lobby self={self} room={room} notify={notify} onOpenArchive={() => setChefArchiveOpen(true)} />
        ) : room?.phase === 'playing' ? (
          self.isSpectator
            ? <SpectatorPlayingScreen room={room} />
            : <GameScreen self={self} room={room} task={currentSketchTask(task, room)} notify={notify} />
        ) : room?.phase === 'album_waiting' ? (
          <AlbumWaitingScreen self={self} room={room} notify={notify} />
        ) : room?.phase === 'reveal' ? (
          <RevealScreen self={self} chains={chains} room={room} serverClockOffsetMs={serverClockOffsetMs} notify={notify} />
        ) : (
          <Splash />
        )}
      </main>

      {chefArchiveOpen && self?.isChef && selectedMode && (
        <ChefArchiveModal
          token={readPersistentValue(tokenKey(selectedMode)) || ''}
          onClose={() => setChefArchiveOpen(false)}
        />
      )}
      {sharedArchive && selectedMode && (
        <SharedArchiveOverlay
          presentation={sharedArchive}
          token={readPersistentValue(tokenKey(selectedMode)) || ''}
          onClose={() => setSharedArchive(null)}
        />
      )}
      {startCountdownEndsAt && <GameStartCountdown endsAt={startCountdownEndsAt} />}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function SharedArchiveOverlay({ presentation, token, onClose }: { presentation: SharedArchivePresentation; token: string; onClose: () => void }) {
  const [gifUrl, setGifUrl] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    let objectUrl = '';
    setGifUrl('');
    setError('');
    fetch(presentation.gifUrl, { headers: token ? { Authorization: `Bearer ${token}` } : {}, cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.error || 'تعذر تحميل اللعبة من الأرشيف.');
        }
        return response.blob();
      })
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setGifUrl(objectUrl);
      })
      .catch((err) => { if (active) setError(messageOf(err)); });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [presentation.id, presentation.gifUrl, token]);

  return (
    <div className="shared-archive-backdrop" role="dialog" aria-modal="true" aria-label="عرض لعبة من الأرشيف">
      <section className="shared-archive-card">
        <header>
          <div><span>🎞 عرض من الأرشيف</span><h2>{presentation.title || 'SketchRelay'}</h2><small>{formatAdminDateTime(presentation.startedAt)}</small></div>
          <button type="button" onClick={onClose} aria-label="إغلاق">×</button>
        </header>
        {error && <div className="chef-archive-error">{error}</div>}
        {!gifUrl && !error && <div className="shared-archive-loading">جاري تحميل العنصر المحفوظ…</div>}
        {gifUrl && <img src={gifUrl} alt={`عنصر الأرشيف ${presentation.title || 'SketchRelay'}`} />}
      </section>
    </div>
  );
}

function GameStartCountdown({ endsAt }: { endsAt: number }) {
  const [remaining, setRemaining] = useState(() => Math.max(1, Math.ceil((endsAt - Date.now()) / 1000)));

  useEffect(() => {
    const update = () => setRemaining(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));
    update();
    const timer = window.setInterval(update, 50);
    return () => window.clearInterval(timer);
  }, [endsAt]);

  if (remaining <= 0) return null;

  return (
    <div className="game-start-countdown" role="status" aria-live="assertive" aria-label={`تبدأ اللعبة خلال ${remaining}`}>
      <div className="game-start-countdown-inner" key={remaining}>
        <span>استعد!</span>
        <strong>{remaining}</strong>
      </div>
    </div>
  );
}

type AdminGameType = 'sketch' | 'who-said' | 'story' | 'blind' | 'marriage';
type AdminHistoryStatus = 'active' | 'completed' | 'abandoned';

interface AdminHistoryPlayer { id?: string; nickname: string; isChef?: boolean; }
interface AdminHistorySummary {
  id: string;
  gameType: AdminGameType;
  status: AdminHistoryStatus;
  startedAt: number;
  endedAt: number | null;
  durationMs: number | null;
  hostName: string;
  playerCount: number;
  players: AdminHistoryPlayer[];
  title: string;
  archiveGifAvailable?: boolean;
  archiveSourceAvailable?: boolean;
  archiveKind?: 'round' | 'full-game';
  roundNumber?: number;
  sourceGameId?: string;
}
interface AdminHistoryRecord extends Omit<AdminHistorySummary, 'playerCount'> {
  settings?: unknown;
  details?: any;
  updatedAt: number;
  archive?: { gifAvailable: boolean; sourceAvailable?: boolean; gifUrl?: string };
}
interface AdminOverview {
  stats: {
    totalSessions: number;
    sessionsToday: number;
    completedSessions: number;
    abandonedSessions: number;
    uniquePlayers: number;
    participantSlots: number;
    byGame: Record<AdminGameType, number>;
    last7Days: Array<{ day: string; count: number }>;
  };
  storage: { backend: string; retentionDays: number; maxSessions: number };
  live: Array<{
    gameType: AdminGameType;
    phase: string;
    active: boolean;
    sessionId: string | null;
    connectedPlayers: number;
    playerCount: number;
    spectatorCount: number;
    players: Array<{ nickname: string; isChef: boolean; connected: boolean }>;
  }>;
  serverTime: number;
}
interface AdminHistoryPage {
  items: AdminHistorySummary[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface SketchArchiveSourcePayload {
  version?: number;
  sessionId?: string;
  canvasFormat?: GameSettings['canvasFormat'];
  settings?: Partial<GameSettings>;
  chains: GameChain[];
}

async function createAndUploadFullGameArchive(
  sessionId: string,
  token: string,
  onProgress?: (percent: number, label: string) => void
): Promise<number> {
  const sourceResponse = await fetch(`/api/sketch/archive/${encodeURIComponent(sessionId)}/source`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store'
  });
  const sourcePayload = await sourceResponse.json().catch(() => ({}));
  if (!sourceResponse.ok || sourcePayload.ok === false) throw new Error(sourcePayload.error || 'تعذر تحميل مصدر الأرشيف.');
  const source = sourcePayload.source as SketchArchiveSourcePayload | undefined;
  if (!source?.chains?.length) throw new Error('مصدر الأرشيف لا يحتوي بيانات اللعبة الكاملة.');

  const blob = await createAlbumGifBlob(
    source.chains,
    source.canvasFormat || source.settings?.canvasFormat || 'standard',
    onProgress,
    { animateDrawings: false }
  );

  // Upload retries are safe because the endpoint atomically replaces the same
  // session GIF. This handles brief mobile/shared-hosting network stalls without
  // generating duplicate archive entries.
  let lastError = 'تعذر رفع GIF إلى الأرشيف.';
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      onProgress?.(98, attempt === 1 ? 'رفع GIF إلى الأرشيف…' : `إعادة محاولة الرفع ${attempt}/3…`);
      const response = await fetch(`/api/sketch/archive/${encodeURIComponent(sessionId)}/gif`, {
        method: 'POST',
        headers: {
          'Content-Type': 'image/gif',
          Authorization: `Bearer ${token}`
        },
        body: blob
      });
      const payload = await response.json().catch(() => ({}));
      if (response.ok && payload.ok !== false) {
        onProgress?.(100, 'تم حفظ GIF الكامل.');
        return Number(payload.bytes || blob.size);
      }
      lastError = payload.error || lastError;
    } catch (error) {
      lastError = messageOf(error);
    }
    if (attempt < 3) await new Promise<void>((resolve) => window.setTimeout(resolve, attempt * 1200));
  }
  throw new Error(lastError);
}


async function createAndUploadRoundArchive(
  gameId: string,
  chainIndex: number,
  chain: GameChain,
  canvasFormat: GameSettings['canvasFormat'],
  token: string,
  onProgress?: (percent: number, label: string) => void
): Promise<{ id: string; bytes: number; alreadySaved?: boolean }> {
  if (!token) throw new Error('جلسة الأدمن غير متوفرة لحفظ الجولة.');
  const blob = await createAlbumGifBlob([chain], canvasFormat, onProgress, { animateDrawings: false });

  let lastError = 'تعذر حفظ الجولة في الأرشيف.';
  for (let attempt = 1; attempt <= 3; attempt += 1) {
    try {
      onProgress?.(98, attempt === 1 ? 'رفع الجولة إلى الأرشيف…' : `إعادة محاولة الحفظ ${attempt}/3…`);
      const response = await fetch(`/api/sketch/archive/round/${encodeURIComponent(gameId)}/${chainIndex}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'image/gif',
          Authorization: `Bearer ${token}`
        },
        body: blob,
        cache: 'no-store'
      });
      const payload = await response.json().catch(() => ({}));
      if (response.ok && payload.ok !== false) {
        onProgress?.(100, payload.alreadySaved ? 'الجولة محفوظة مسبقًا.' : 'تم حفظ الجولة في الأرشيف.');
        return { id: String(payload.id || ''), bytes: Number(payload.bytes || blob.size), alreadySaved: Boolean(payload.alreadySaved) };
      }
      lastError = payload.error || lastError;
    } catch (error) {
      lastError = messageOf(error);
    }
    if (attempt < 3) await new Promise<void>((resolve) => window.setTimeout(resolve, attempt * 1000));
  }
  throw new Error(lastError);
}

function ChefArchiveModal({ token, onClose }: { token: string; onClose: () => void }) {
  const [historyPage, setHistoryPage] = useState<AdminHistoryPage | null>(null);
  const [detail, setDetail] = useState<AdminHistoryRecord | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ q: '', game: '', status: 'completed', page: 1 });

  const api = useCallback(async (url: string, init?: RequestInit) => {
    if (!token) throw new Error('جلسة الأدمن غير متوفرة. أعد الدخول إلى اللعبة كأدمن.');
    const response = await fetch(url, {
      ...init,
      headers: { Authorization: `Bearer ${token}`, ...(init?.headers || {}) },
      cache: 'no-store'
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || data.ok === false) throw new Error(data.error || 'تعذر تحميل الأرشيف.');
    return data;
  }, [token]);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(filters.page));
      params.set('pageSize', '20');
      if (filters.q.trim()) params.set('q', filters.q.trim());
      if (filters.game) params.set('game', filters.game);
      if (filters.status) params.set('status', filters.status);
      const data = await api(`/api/chef/archive/history?${params.toString()}`);
      setHistoryPage({
        items: data.items,
        page: data.page,
        pageSize: data.pageSize,
        total: data.total,
        totalPages: data.totalPages
      });
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setLoading(false);
    }
  }, [api, filters]);

  useEffect(() => {
    const timer = window.setTimeout(loadHistory, 160);
    return () => window.clearTimeout(timer);
  }, [loadHistory]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !detail) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, detail]);

  const openDetail = async (id: string) => {
    setDetailBusy(true);
    setError('');
    try {
      const data = await api(`/api/chef/archive/history/${encodeURIComponent(id)}`);
      setDetail(data.session);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setDetailBusy(false);
    }
  };

  const showArchiveToPlayers = async (item: AdminHistorySummary) => {
    if (!item.archiveGifAvailable) return setError('هذا العنصر لا يحتوي GIF محفوظًا.');
    setDetailBusy(true);
    setError('');
    try {
      const data = await api(`/api/chef/archive/history/${encodeURIComponent(item.id)}/show`, { method: 'POST' });
      setError('');
      window.alert(`تم عرض ${item.archiveKind === 'round' ? `الجولة ${item.roundNumber ?? ''}` : 'اللعبة الكاملة'} للموجودين الآن (${data.viewers ?? 0} اتصال).`);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setDetailBusy(false);
    }
  };

  const deleteArchive = async (item: AdminHistorySummary) => {
    if (item.status === 'active') return setError('لا يمكن حذف لعبة جارية.');
    if (!window.confirm(`حذف ${item.archiveKind === 'round' ? 'الجولة' : 'اللعبة'} "${item.title || adminGameLabel(item.gameType)}" من الأرشيف نهائيًا؟`)) return;
    setDetailBusy(true);
    setError('');
    try {
      await api(`/api/chef/archive/history/${encodeURIComponent(item.id)}`, { method: 'DELETE' });
      setDetail((current) => current?.id === item.id ? null : current);
      await loadHistory();
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setDetailBusy(false);
    }
  };

  const patchFilter = (key: 'q' | 'game' | 'status', value: string) => {
    setFilters((current) => ({ ...current, [key]: value, page: 1 }));
  };

  return (
    <div className="chef-archive-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target && !detail) onClose(); }}>
      <section className="chef-archive-modal" role="dialog" aria-modal="true" aria-label="أرشيف الألعاب">
        <header className="chef-archive-head">
          <div>
            <span className="eyebrow">GAME ARCHIVE</span>
            <h2>🗂️ أرشيف الألعاب</h2>
            <p>في SketchRelay لا يُحفظ شيء تلقائيًا. كل جولة يختار الأدمن حفظها تظهر كعنصر مستقل. الأرشيفات القديمة الكاملة تبقى متاحة كما هي.</p>
          </div>
          <button className="chef-archive-close" type="button" onClick={onClose} aria-label="إغلاق الأرشيف">×</button>
        </header>

        <div className="chef-archive-toolbar">
          <label className="chef-archive-search">
            <span>بحث</span>
            <input value={filters.q} onChange={(e) => patchFilter('q', e.target.value)} placeholder="اسم اللاعب أو عنوان الجلسة..." />
          </label>
          <label>
            <span>اللعبة</span>
            <select value={filters.game} onChange={(e) => patchFilter('game', e.target.value)}>
              <option value="">كل الألعاب</option>
              <option value="sketch">SketchRelay</option>
              <option value="who-said">من قال هذا؟</option>
              <option value="story">أكمل القصة</option>
              <option value="blind">ارسم وأنت أعمى</option><option value="marriage">الزواج الأعمى</option>
            </select>
          </label>
          <label>
            <span>الحالة</span>
            <select value={filters.status} onChange={(e) => patchFilter('status', e.target.value)}>
              <option value="">كل الحالات</option>
              <option value="completed">مكتملة</option>
              <option value="active">جارية</option>
              <option value="abandoned">أُوقفت</option>
            </select>
          </label>
          <button className="secondary-btn chef-archive-refresh" type="button" onClick={loadHistory} disabled={loading}>↻ تحديث</button>
        </div>

        {error && <div className="chef-archive-error">{error}</div>}

        <div className="chef-archive-summary">
          <strong>{historyPage?.total ?? 0}</strong>
          <span>عنصر محفوظ</span>
          {loading && <small>جاري التحديث…</small>}
        </div>

        <div className="chef-archive-list">
          {!loading && historyPage?.items.length === 0 && (
            <div className="chef-archive-empty">
              <span>🗂️</span>
              <strong>لا توجد جلسات مطابقة</strong>
              <p>ستظهر هنا فقط الألعاب أو جولات SketchRelay التي تم حفظها فعليًا في الأرشيف.</p>
            </div>
          )}

          {(historyPage?.items ?? []).map((item) => (
            <article className="chef-archive-item" key={item.id}>
              <div className={`chef-archive-game-icon ${item.gameType}`}>{adminGameIcon(item.gameType)}</div>
              <div className="chef-archive-item-main">
                <div className="chef-archive-item-title">
                  <strong>{item.title || adminGameLabel(item.gameType)}</strong>
                  <span className={`admin-status ${item.status}`}>{adminStatusLabel(item.status)}</span>
                  {item.archiveGifAvailable && <span className="chef-archive-gif-badge">{item.archiveKind === 'round' ? `🎞 GIF الجولة ${item.roundNumber ?? ''}` : '🎞 GIF كامل للعبة'}</span>}
                  {!item.archiveGifAvailable && item.archiveSourceAvailable && <span className="chef-archive-gif-badge">🛠 GIF قابل لإعادة الإنشاء</span>}
                </div>
                <small>{adminGameLabel(item.gameType)} · {formatAdminDateTime(item.startedAt)} · {formatAdminDuration(item.durationMs)}</small>
                <div className="chef-archive-item-players">
                  <b>👑 {item.hostName}</b>
                  <span>👥 {item.playerCount} لاعبين</span>
                  {item.players.slice(0, 4).map((player, index) => <em key={`${item.id}-${player.nickname}-${index}`}>{player.nickname}</em>)}
                  {item.playerCount > 4 && <em>+{item.playerCount - 4}</em>}
                </div>
              </div>
              <div className="chef-archive-actions">
                <button className="chef-archive-open" type="button" onClick={() => openDetail(item.id)} disabled={detailBusy}>👁 عرض</button>
                {item.gameType === 'sketch' && item.archiveGifAvailable && (
                  <button className="chef-archive-show" type="button" onClick={() => showArchiveToPlayers(item)} disabled={detailBusy}>📺 عرض للجميع</button>
                )}
                <button className="chef-archive-delete" type="button" onClick={() => deleteArchive(item)} disabled={detailBusy || item.status === 'active'}>🗑 حذف</button>
              </div>
            </article>
          ))}
        </div>

        {historyPage && historyPage.totalPages > 1 && (
          <footer className="chef-archive-pagination">
            <button type="button" disabled={historyPage.page <= 1 || loading} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}>السابق</button>
            <span>صفحة <b>{historyPage.page}</b> من <b>{historyPage.totalPages}</b></span>
            <button type="button" disabled={historyPage.page >= historyPage.totalPages || loading} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}>التالي</button>
          </footer>
        )}
      </section>

      {detail && <ChefArchiveDetailDrawer session={detail} token={token} onClose={() => setDetail(null)} onDeleted={() => { setDetail(null); void loadHistory(); }} />}
      {detailBusy && <div className="admin-detail-loading">جاري فتح تفاصيل الجلسة…</div>}
    </div>
  );
}

function ChefArchiveDetailDrawer({ session, token, onClose, onDeleted }: { session: AdminHistoryRecord; token: string; onClose: () => void; onDeleted: () => void }) {
  const [gifUrl, setGifUrl] = useState('');
  const [generated, setGenerated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rebuildProgress, setRebuildProgress] = useState(0);
  const [rebuildLabel, setRebuildLabel] = useState('');
  const [error, setError] = useState('');

  const gifAvailable = Boolean(session.archive?.gifAvailable || generated);
  const sourceAvailable = Boolean(session.archive?.sourceAvailable && !generated);
  const gifEndpoint = session.archive?.gifUrl || `/api/chef/archive/history/${encodeURIComponent(session.id)}/gif`;
  const isSketchRound = session.gameType === 'sketch' && session.details?.archiveKind === 'round';
  const savedRoundNumber = Number.isInteger(session.details?.roundNumber) ? Number(session.details.roundNumber) : undefined;

  useEffect(() => {
    if (session.gameType !== 'sketch' || !gifAvailable) {
      setGifUrl('');
      return;
    }
    let active = true;
    let objectUrl = '';
    setError('');
    fetch(gifEndpoint, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.error || 'تعذر تحميل GIF المحفوظ.');
        }
        return response.blob();
      })
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setGifUrl(objectUrl);
      })
      .catch((err) => { if (active) setError(messageOf(err)); });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [session.id, session.gameType, gifAvailable, gifEndpoint, token]);

  const request = async (url: string, method: 'POST' | 'DELETE') => {
    const response = await fetch(url, { method, headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || payload.ok === false) throw new Error(payload.error || 'تعذر تنفيذ العملية.');
    return payload;
  };

  const rebuildGif = async () => {
    if (session.gameType !== 'sketch' || isSketchRound || !sourceAvailable || busy) return;
    setBusy(true);
    setError('');
    setRebuildProgress(0);
    setRebuildLabel('تحميل مصدر اللعبة...');
    try {
      await createAndUploadFullGameArchive(session.id, token, (percent, label) => {
        setRebuildProgress(percent);
        setRebuildLabel(label);
      });
      setGenerated(true);
      setRebuildProgress(100);
      setRebuildLabel('تم إنشاء GIF الكامل وحفظه.');
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const showToEveryone = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await request(`/api/chef/archive/history/${encodeURIComponent(session.id)}/show`, 'POST');
      window.alert(`تم إرسال ${isSketchRound ? `الجولة ${savedRoundNumber ?? ''}` : 'اللعبة الكاملة'} إلى جميع الموجودين (${result.viewers ?? 0} اتصال).`);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  const deleteGame = async () => {
    if (!window.confirm(`حذف ${isSketchRound ? `الجولة ${savedRoundNumber ?? ''}` : 'هذه اللعبة كاملة'} من الأرشيف؟\n${session.title || adminGameLabel(session.gameType)}`)) return;
    setBusy(true);
    setError('');
    try {
      await request(`/api/chef/archive/history/${encodeURIComponent(session.id)}`, 'DELETE');
      onDeleted();
    } catch (err) {
      setError(messageOf(err));
      setBusy(false);
    }
  };

  return (
    <div className="admin-drawer-backdrop chef-archive-detail-backdrop" role="presentation" onMouseDown={(event) => { if (event.currentTarget === event.target) onClose(); }}>
      <aside className="admin-drawer chef-archive-detail-drawer" role="dialog" aria-modal="true" aria-label="اللعبة المحفوظة">
        <header className="admin-drawer-head">
          <div>
            <span className="eyebrow">{isSketchRound ? 'SAVED ROUND ARCHIVE' : 'FULL GAME ARCHIVE'}</span>
            <h2>{session.title || adminGameLabel(session.gameType)}</h2>
            <p>{adminGameLabel(session.gameType)} · {formatAdminDateTime(session.startedAt)} · {formatAdminDuration(session.durationMs)} · {session.players.length} لاعبين</p>
          </div>
          <button type="button" onClick={onClose} aria-label="إغلاق">×</button>
        </header>

        {error && <div className="chef-archive-error">{error}</div>}

        {session.gameType === 'sketch' ? (
          <section className="chef-full-game-gif">
            <div className="chef-full-game-copy">
              <strong>{isSketchRound ? `🎞 الجولة ${savedRoundNumber ?? ''} محفوظة` : '🎞 ملف واحد للعبة كاملة'}</strong>
              <p>{isSketchRound ? 'هذا العنصر يحتوي الجولة التي اختار الأدمن حفظها فقط. بقية جولات اللعبة لا تُحفظ تلقائيًا.' : 'هذا أرشيف قديم من النسخ السابقة ويحتوي جميع جولات اللعبة داخل ملف واحد.'}</p>
            </div>

            {!isSketchRound && !gifAvailable && sourceAvailable && (
              <div className="chef-archive-empty">
                <strong>GIF غير مكتمل، لكن مصدر اللعبة محفوظ بأمان</strong>
                <p>يمكن إعادة إنشاء الملف الآن من النسخة الدائمة على الخادم بدون فقدان اللعبة.</p>
                <button className="primary-btn" type="button" onClick={rebuildGif} disabled={busy}>🛠 إنشاء/إصلاح GIF الآن</button>
                {(busy || rebuildProgress > 0) && (
                  <div className="gif-export-progress" aria-live="polite">
                    <div className="gif-export-progress-track"><span style={{ width: `${rebuildProgress}%` }} /></div>
                    <small>{rebuildLabel || `جاري الإنشاء… ${rebuildProgress}%`}</small>
                  </div>
                )}
              </div>
            )}

            {!gifAvailable && !sourceAvailable && (
              <div className="chef-archive-empty">
                <strong>{isSketchRound ? 'لا يوجد GIF لهذه الجولة' : 'لا يوجد GIF لهذه اللعبة'}</strong>
                <p>{isSketchRound ? 'تعذر العثور على ملف الجولة المحفوظ.' : 'هذه جلسة قديمة لا تحتوي ملف GIF ولا مصدرًا محفوظًا يمكن إعادة بنائه.'}</p>
              </div>
            )}

            {gifAvailable && !gifUrl && !error && <div className="shared-archive-loading">جاري تحميل {isSketchRound ? 'الجولة المحفوظة' : 'اللعبة الكاملة'}…</div>}
            {gifUrl && (
              <div className="chef-full-game-gif-frame">
                <img src={gifUrl} alt={`${isSketchRound ? 'GIF الجولة المحفوظة' : 'GIF اللعبة الكاملة'} ${session.title || 'SketchRelay'}`} />
              </div>
            )}
          </section>
        ) : (
          <section className="chef-full-game-gif">
            <div className="chef-full-game-copy">
              <strong>{adminGameIcon(session.gameType)} لعبة كاملة محفوظة</strong>
              <p>هذه اللعبة محفوظة كسجل جلسة واحد بتاريخها واللاعبين والنتيجة. إنشاء GIF كامل متاح حاليًا لـSketchRelay.</p>
            </div>
          </section>
        )}

        <div className="chef-full-game-actions">
          {session.gameType === 'sketch' && gifAvailable && gifUrl && (
            <a className="secondary-btn" href={gifUrl} download={isSketchRound ? `SketchRelay-Round-${savedRoundNumber ?? 1}-${session.id}.gif` : `SketchRelay-FullGame-${session.id}.gif`}>⬇ تنزيل GIF</a>
          )}
          {session.gameType === 'sketch' && gifAvailable && (
            <button className="primary-btn" type="button" onClick={showToEveryone} disabled={busy}>📺 عرض للجميع</button>
          )}
          <button className="danger-btn chef-delete-full-game" type="button" onClick={deleteGame} disabled={busy || session.status === 'active'}>🗑 حذف {isSketchRound ? 'الجولة' : 'اللعبة'}</button>
          <button className="secondary-btn" type="button" onClick={onClose} disabled={busy}>إغلاق</button>
        </div>
      </aside>
    </div>
  );
}

function AdminDashboard() {
  const [token, setToken] = useState(() => sessionStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const [password, setPassword] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);
  const [error, setError] = useState('');
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [historyPage, setHistoryPage] = useState<AdminHistoryPage | null>(null);
  const [detail, setDetail] = useState<AdminHistoryRecord | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ q: '', game: '', status: '', from: '', to: '', page: 1, pageSize: 25 });

  const logoutAdmin = useCallback(() => {
    sessionStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken('');
    setOverview(null);
    setHistoryPage(null);
    setDetail(null);
  }, []);

  const api = useCallback(async (url: string, init?: RequestInit) => {
    const response = await fetch(url, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers || {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      }
    });
    const data = await response.json().catch(() => ({}));
    if (response.status === 401 && token) {
      logoutAdmin();
      throw new Error(data.error || 'انتهت جلسة لوحة التحكم.');
    }
    if (!response.ok || data.ok === false) throw new Error(data.error || 'تعذر تحميل البيانات.');
    return data;
  }, [token, logoutAdmin]);

  const login = async (event: FormEvent) => {
    event.preventDefault();
    if (!password.trim()) return;
    setLoginBusy(true);
    setError('');
    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.token) throw new Error(data.error || 'تعذر تسجيل الدخول.');
      sessionStorage.setItem(ADMIN_TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword('');
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setLoginBusy(false);
    }
  };

  const loadOverview = useCallback(async () => {
    if (!token) return;
    try {
      const data = await api('/api/admin/overview');
      setOverview({ stats: data.stats, storage: data.storage, live: data.live, serverTime: data.serverTime });
    } catch (err) {
      setError(messageOf(err));
    }
  }, [api, token]);

  const loadHistory = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(filters.page));
      params.set('pageSize', String(filters.pageSize));
      if (filters.q.trim()) params.set('q', filters.q.trim());
      if (filters.game) params.set('game', filters.game);
      if (filters.status) params.set('status', filters.status);
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);
      const data = await api(`/api/admin/history?${params.toString()}`);
      setHistoryPage({ items: data.items, page: data.page, pageSize: data.pageSize, total: data.total, totalPages: data.totalPages });
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setLoading(false);
    }
  }, [api, filters, token]);

  useEffect(() => {
    if (!token) return;
    loadOverview();
    const timer = window.setInterval(loadOverview, 15_000);
    return () => window.clearInterval(timer);
  }, [token, loadOverview]);

  useEffect(() => {
    if (!token) return;
    const timer = window.setTimeout(loadHistory, 220);
    return () => window.clearTimeout(timer);
  }, [token, loadHistory]);

  const openDetail = async (id: string) => {
    setDetailBusy(true);
    setError('');
    try {
      const data = await api(`/api/admin/history/${encodeURIComponent(id)}`);
      setDetail(data.session);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setDetailBusy(false);
    }
  };

  const updateFilter = (key: keyof typeof filters, value: string | number) => {
    setFilters((current) => ({ ...current, [key]: value, page: key === 'page' ? Number(value) : 1 }));
  };

  if (!token) {
    return (
      <div className="admin-shell">
        <AmbientBackground />
        <main className="admin-login-wrap">
          <form className="admin-login-card" onSubmit={login}>
            <span className="admin-lock">🔐</span>
            <div><span className="eyebrow">MBB PARTY GAMES</span><h1>لوحة الإدارة</h1><p>دخول محمي لمتابعة الألعاب وأرشيف الجلسات الدائم.</p></div>
            <label>كلمة مرور لوحة التحكم</label>
            <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            {error && <div className="admin-error">{error}</div>}
            <button className="primary-btn" type="submit" disabled={loginBusy}>{loginBusy ? 'جاري الدخول...' : 'دخول لوحة التحكم'}</button>
            <a className="admin-back-link" href="/">← العودة إلى الألعاب</a>
          </form>
        </main>
      </div>
    );
  }

  const stats = overview?.stats;
  const maxDaily = Math.max(1, ...(stats?.last7Days.map((d) => d.count) ?? [1]));

  return (
    <div className="admin-shell">
      <header className="admin-topbar">
        <div className="admin-brand"><span className="brand-mark company-logo-mark"><img src="/brand/mbb-logo-mark.png" alt="MBB Websolutions" /></span><div><strong>MBB Party Games</strong><small>لوحة الإدارة والأرشيف</small></div></div>
        <div className="admin-top-actions"><a href="/" className="ghost-btn">فتح الألعاب ↗</a><button className="ghost-btn" type="button" onClick={logoutAdmin}>تسجيل الخروج</button></div>
      </header>

      <main className="admin-page">
        <section className="admin-heading">
          <div><span className="eyebrow">CONTROL CENTER</span><h1>نظرة شاملة على الألعاب</h1><p>الجلسات الحالية، الإحصاءات، وكل ما لعبه المشاركون محفوظ ومنظم وقابل للبحث.</p></div>
          <button className="secondary-btn" type="button" onClick={() => { loadOverview(); loadHistory(); }}>↻ تحديث البيانات</button>
        </section>

        {error && <div className="admin-error admin-global-error"><span>{error}</span><button type="button" onClick={() => setError('')}>×</button></div>}

        <section className="admin-kpis">
          <AdminKpi icon="🎮" label="إجمالي الجلسات" value={stats?.totalSessions ?? 0} note={`${stats?.completedSessions ?? 0} مكتملة`} />
          <AdminKpi icon="⚡" label="جلسات اليوم" value={stats?.sessionsToday ?? 0} note="منذ منتصف الليل" />
          <AdminKpi icon="👥" label="لاعبون مختلفون" value={stats?.uniquePlayers ?? 0} note={`${stats?.participantSlots ?? 0} مشاركة إجمالية`} />
          <AdminKpi icon="🗂️" label="سعة السجل" value={overview ? `${overview.storage.retentionDays} يوم` : '—'} note={overview ? `${overview.storage.backend.toUpperCase()} · حتى ${overview.storage.maxSessions.toLocaleString('ar')} جلسة` : 'جاري التحميل'} />
        </section>

        <section className="admin-overview-grid">
          <div className="admin-panel">
            <div className="admin-panel-head"><div><h2>الألعاب الآن</h2><p>حالة كل غرفة لحظيًا.</p></div><span className="admin-auto-refresh">● تحديث تلقائي</span></div>
            <div className="admin-live-grid">
              {(overview?.live ?? []).map((room) => <AdminLiveRoom key={room.gameType} room={room} />)}
              {!overview && <div className="admin-loading-block">جاري تحميل الغرف…</div>}
            </div>
          </div>

          <div className="admin-panel admin-chart-panel">
            <div className="admin-panel-head"><div><h2>آخر 7 أيام</h2><p>عدد الجلسات التي بدأت يوميًا.</p></div></div>
            <div className="admin-mini-chart">
              {(stats?.last7Days ?? []).map((item) => (
                <div className="admin-bar-item" key={item.day} title={`${item.day}: ${item.count}`}>
                  <span className="admin-bar-value">{item.count}</span>
                  <div className="admin-bar-track"><i style={{ height: `${Math.max(5, (item.count / maxDaily) * 100)}%` }} /></div>
                  <small>{item.day.slice(5)}</small>
                </div>
              ))}
            </div>
            <div className="admin-game-totals">
              <span><i className="admin-dot sketch" />SketchRelay <b>{stats?.byGame.sketch ?? 0}</b></span>
              <span><i className="admin-dot who" />من قال هذا؟ <b>{stats?.byGame['who-said'] ?? 0}</b></span>
              <span><i className="admin-dot story" />أكمل القصة <b>{stats?.byGame.story ?? 0}</b></span>
              <span><i className="admin-dot blind" />ارسم وأنت أعمى <b>{stats?.byGame.blind ?? 0}</b></span>
              <span><i className="admin-dot marriage" />الزواج الأعمى <b>{stats?.byGame.marriage ?? 0}</b></span>
            </div>
          </div>
        </section>

        <section className="admin-panel admin-history-panel">
          <div className="admin-panel-head history-head"><div><h2>أرشيف الألعاب</h2><p>كل العناصر المحفوظة قابلة للبحث والمراجعة. في SketchRelay تظهر فقط الجولات التي اختار الأدمن حفظها يدويًا.</p></div><strong>{historyPage?.total ?? 0} جلسة</strong></div>

          <div className="admin-filters">
            <label className="admin-search"><span>⌕</span><input value={filters.q} onChange={(e) => updateFilter('q', e.target.value)} placeholder="بحث بالاسم، السؤال، أو بداية القصة…" /></label>
            <select value={filters.game} onChange={(e) => updateFilter('game', e.target.value)}><option value="">كل الألعاب</option><option value="sketch">SketchRelay</option><option value="who-said">من قال هذا؟</option><option value="story">أكمل القصة</option><option value="blind">ارسم وأنت أعمى</option><option value="marriage">الزواج الأعمى</option></select>
            <select value={filters.status} onChange={(e) => updateFilter('status', e.target.value)}><option value="">كل الحالات</option><option value="completed">مكتملة</option><option value="active">جارية</option><option value="abandoned">أُوقفت</option></select>
            <label className="admin-date-filter"><span>من</span><input type="date" value={filters.from} onChange={(e) => updateFilter('from', e.target.value)} /></label>
            <label className="admin-date-filter"><span>إلى</span><input type="date" value={filters.to} onChange={(e) => updateFilter('to', e.target.value)} /></label>
            <select value={filters.pageSize} onChange={(e) => updateFilter('pageSize', Number(e.target.value))}><option value={25}>25 / صفحة</option><option value={50}>50 / صفحة</option><option value={100}>100 / صفحة</option></select>
          </div>

          <div className="admin-history-table-wrap">
            <table className="admin-history-table">
              <thead><tr><th>الوقت</th><th>اللعبة</th><th>المحتوى</th><th>اللاعبون</th><th>المدة</th><th>الحالة</th><th /></tr></thead>
              <tbody>
                {loading && !historyPage && <tr><td colSpan={7} className="admin-empty">جاري تحميل السجل…</td></tr>}
                {!loading && historyPage?.items.length === 0 && <tr><td colSpan={7} className="admin-empty">لا توجد جلسات مطابقة لهذه الفلاتر.</td></tr>}
                {historyPage?.items.map((item) => (
                  <tr key={item.id}>
                    <td><strong>{formatAdminDate(item.startedAt)}</strong><small>{formatAdminTime(item.startedAt)}</small></td>
                    <td><span className={`admin-game-badge ${item.gameType}`}>{adminGameIcon(item.gameType)} {adminGameLabel(item.gameType)}</span></td>
                    <td className="admin-title-cell"><strong title={item.title}>{item.title || 'بدون عنوان'}</strong><small>الأدمن: {item.hostName}</small>{item.archiveGifAvailable && <span className="admin-archive-badge">{item.archiveKind === 'round' ? `🎞 الجولة ${item.roundNumber ?? ''}` : '🎞 GIF محفوظ'}</span>}</td>
                    <td><div className="admin-player-stack">{item.players.slice(0, 4).map((player, index) => <FunnyAvatar key={`${item.id}-${player.nickname}-${index}`} name={player.nickname} className="admin-table-avatar" />)}{item.playerCount > 4 && <b>+{item.playerCount - 4}</b>}</div><small>{item.playerCount} لاعبين</small></td>
                    <td>{formatAdminDuration(item.durationMs)}</td>
                    <td><span className={`admin-status ${item.status}`}>{adminStatusLabel(item.status)}</span></td>
                    <td><button className="admin-view-btn" type="button" onClick={() => openDetail(item.id)} disabled={detailBusy}>عرض التفاصيل</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {historyPage && historyPage.totalPages > 1 && (
            <div className="admin-pagination">
              <button type="button" disabled={historyPage.page <= 1} onClick={() => updateFilter('page', historyPage.page - 1)}>السابق</button>
              <span>صفحة <b>{historyPage.page}</b> من <b>{historyPage.totalPages}</b></span>
              <button type="button" disabled={historyPage.page >= historyPage.totalPages} onClick={() => updateFilter('page', historyPage.page + 1)}>التالي</button>
            </div>
          )}
        </section>
      </main>

      {detail && <AdminSessionDrawer session={detail} adminToken={token} onClose={() => setDetail(null)} />}
      {detailBusy && <div className="admin-detail-loading">جاري فتح تفاصيل الجلسة…</div>}
    </div>
  );
}

function AdminKpi({ icon, label, value, note }: { icon: string; label: string; value: string | number; note: string }) {
  return <article className="admin-kpi"><span>{icon}</span><div><small>{label}</small><strong>{typeof value === 'number' ? value.toLocaleString('ar') : value}</strong><p>{note}</p></div></article>;
}

function AdminLiveRoom({ room }: { room: AdminOverview['live'][number] }) {
  return (
    <article className={`admin-live-room ${room.gameType} ${room.active ? 'active' : ''}`}>
      <div className="admin-live-title"><span>{adminGameIcon(room.gameType)}</span><div><strong>{adminGameLabel(room.gameType)}</strong><small>{adminPhaseLabel(room.phase)}</small></div><i /></div>
      <div className="admin-live-count"><b>{room.connectedPlayers}</b><span>متصل الآن</span></div>
      <div className="admin-live-players">{room.players.length ? room.players.slice(0, 5).map((p, index) => <span key={`${p.nickname}-${index}`} className={!p.connected ? 'offline' : ''}>{p.isChef ? '👑 ' : ''}{p.nickname}</span>) : <small>الغرفة فارغة</small>}{room.players.length > 5 && <small>+{room.players.length - 5}</small>}</div>
    </article>
  );
}

function AdminSessionDrawer({ session, adminToken, onClose }: { session: AdminHistoryRecord; adminToken: string; onClose: () => void }) {
  const details = session.details || {};
  const isSketchRound = session.gameType === 'sketch' && details.archiveKind === 'round';
  const savedRoundNumber = Number.isInteger(details.roundNumber) ? Number(details.roundNumber) : undefined;
  const [archiveGifUrl, setArchiveGifUrl] = useState('');
  const [archiveGifLoading, setArchiveGifLoading] = useState(false);
  const [archiveGifError, setArchiveGifError] = useState('');

  useEffect(() => {
    if (session.gameType !== 'sketch' || !session.archive?.gifAvailable || !session.archive.gifUrl) {
      setArchiveGifUrl('');
      setArchiveGifError('');
      return;
    }
    let active = true;
    let objectUrl = '';
    setArchiveGifLoading(true);
    setArchiveGifError('');
    fetch(session.archive.gifUrl, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.error || 'تعذر تحميل GIF الأرشيف.');
        }
        return response.blob();
      })
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setArchiveGifUrl(objectUrl);
      })
      .catch((error) => { if (active) setArchiveGifError(messageOf(error)); })
      .finally(() => { if (active) setArchiveGifLoading(false); });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [session.id, session.gameType, session.archive?.gifAvailable, session.archive?.gifUrl, adminToken]);

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(session, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mbb-game-${session.gameType}-${session.id}.json`;
    a.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 500);
  };

  return (
    <div className="admin-drawer-backdrop" role="presentation" onMouseDown={(e) => { if (e.currentTarget === e.target) onClose(); }}>
      <aside className="admin-drawer" role="dialog" aria-modal="true" aria-label="تفاصيل جلسة اللعب">
        <div className="admin-drawer-head"><div><span className={`admin-game-badge ${session.gameType}`}>{adminGameIcon(session.gameType)} {adminGameLabel(session.gameType)}</span><h2>{session.title || adminGameLabel(session.gameType)}</h2><p>{formatAdminDateTime(session.startedAt)} · {formatAdminDuration(session.durationMs)}</p></div><button type="button" onClick={onClose}>×</button></div>

        <div className="admin-detail-meta">
          <div><small>الحالة</small><strong className={`admin-status ${session.status}`}>{adminStatusLabel(session.status)}</strong></div>
          <div><small>الأدمن</small><strong>{session.hostName}</strong></div>
          <div><small>عدد اللاعبين</small><strong>{session.players.length}</strong></div>
          <div><small>معرف الجلسة</small><code>{session.id.slice(0, 12)}…</code></div>
        </div>

        <section className="admin-detail-section"><h3>المشاركون</h3><div className="admin-detail-players">{session.players.map((player, index) => <span key={`${player.nickname}-${index}`}><FunnyAvatar name={player.nickname} className="admin-detail-avatar" />{player.isChef && '👑 '}{player.nickname}</span>)}</div></section>

        {session.gameType === 'sketch' && (
          <section className="admin-detail-section admin-archive-section">
            <h3>{isSketchRound ? `GIF الجولة ${savedRoundNumber ?? ''}` : 'GIF الأرشيف الكامل القديم'}</h3>
            <p className="muted">{isSketchRound ? 'هذه الجولة وحدها تم حفظها يدويًا بواسطة الأدمن.' : `نسخة قديمة تحتوي جميع جولات اللعبة ومحفوظة بتاريخ ${formatAdminDateTime(session.startedAt)}.`}</p>
            {archiveGifLoading && <div className="admin-archive-loading"><div className="spinner" /><span>جاري تحميل GIF…</span></div>}
            {archiveGifError && <p className="admin-archive-error">{archiveGifError}</p>}
            {!session.archive?.gifAvailable && <div className="admin-archive-empty">لا يوجد GIF محفوظ لهذا العنصر.</div>}
            {archiveGifUrl && (
              <div className="admin-archive-gif-card">
                <img src={archiveGifUrl} alt={`${isSketchRound ? `GIF الجولة ${savedRoundNumber ?? ''}` : 'GIF اللعبة الكاملة'} ${session.title || 'SketchRelay'}`} />
                <a className="secondary-btn" href={archiveGifUrl} download={isSketchRound ? `SketchRelay-Round-${savedRoundNumber ?? 1}-${session.id}.gif` : `SketchRelay-${session.id}.gif`}>تنزيل GIF ⬇</a>
              </div>
            )}
          </section>
        )}

        {session.gameType === 'who-said' && <AdminWhoDetail details={details} />}
        {session.gameType === 'story' && <AdminStoryDetail details={details} session={session} />}
        {session.gameType === 'sketch' && <AdminSketchDetail details={details} />}
        {session.gameType === 'blind' && <AdminBlindDetail details={details} />}
        {session.gameType === 'marriage' && <AdminMarriageDetail details={details} />}

        <div className="admin-drawer-actions"><button className="secondary-btn" type="button" onClick={downloadJson}>تنزيل JSON</button><button className="primary-btn" type="button" onClick={onClose}>إغلاق</button></div>
      </aside>
    </div>
  );
}

function AdminWhoDetail({ details }: { details: any }) {
  const results = Array.isArray(details.results) ? details.results : [];
  const scores = Array.isArray(details.scores) ? details.scores : [];
  const guesses = Array.isArray(details.guesses) ? details.guesses : [];
  return (
    <>
      <section className="admin-detail-section"><h3>السؤال</h3><blockquote className="admin-question-block">{details.question || 'غير محفوظ'}</blockquote></section>
      {scores.length > 0 && <section className="admin-detail-section"><h3>الترتيب النهائي</h3><div className="admin-score-grid">{scores.map((score: any, index: number) => <div key={score.playerId || index}><b>#{index + 1}</b><span>{score.nickname}</span><strong>{score.score} نقطة</strong><small>{score.correct} صحيح</small></div>)}</div></section>}
      <section className="admin-detail-section"><h3>الإجابات والتخمينات</h3><div className="admin-answer-list">{results.map((result: any, index: number) => <article key={result.answerId || index} className="admin-answer-card"><header><span>إجابة {index + 1}</span><strong>{result.authorNickname}</strong></header><p>{result.answer}</p><div className="admin-answer-winners"><small>عرفها:</small>{result.correctGuessers?.length ? result.correctGuessers.map((p: any) => <span key={p.id}>{p.nickname}</span>) : <em>لا أحد</em>}</div><div className="admin-vote-distribution">{(result.distribution || []).filter((d: any) => d.votes > 0).map((d: any) => <span key={d.playerId}><b>{d.nickname}</b> {d.percentage}% ({d.votes})</span>)}</div><details><summary>كل التخمينات</summary><div className="admin-guess-list">{guesses.filter((g: any) => g.answerId === result.answerId).map((g: any, i: number) => <span key={`${g.voterId}-${i}`}>{g.voterNickname} ← {g.guessedNickname}</span>)}</div></details></article>)}</div></section>
    </>
  );
}

function AdminStoryDetail({ details, session }: { details: any; session: AdminHistoryRecord }) {
  const contributions = Array.isArray(details.contributions) ? details.contributions : [];
  return (
    <>
      <section className="admin-detail-section"><h3>بداية القصة</h3><blockquote className="admin-question-block">{details.starter || session.title}</blockquote></section>
      {details.fullStory && <section className="admin-detail-section"><h3>القصة الكاملة</h3><div className="admin-full-story">{details.fullStory}</div></section>}
      <section className="admin-detail-section"><h3>تسلسل الكتابة</h3><div className="admin-story-timeline">{contributions.length ? contributions.map((entry: any, index: number) => <article key={entry.id || index}><b>{index + 1}</b><div><strong>{entry.authorNickname}</strong><small>{entry.createdAt ? formatAdminDateTime(entry.createdAt) : ''}</small><p>{entry.text}</p></div></article>) : <p className="muted">لم تُكشف القصة أو لم تُسجل مساهمات قبل إنهاء الجلسة.</p>}</div></section>
    </>
  );
}

function AdminSketchDetail({ details }: { details: any }) {
  const chains = Array.isArray(details.chains) ? details.chains : details.chain ? [details.chain] : [];
  const baseRound = Number.isInteger(details.roundNumber) ? Number(details.roundNumber) : 1;
  return <section className="admin-detail-section"><h3>{details.archiveKind === 'round' ? `الجولة المحفوظة ${baseRound}` : 'الجولات'}</h3><div className="admin-chain-list">{chains.length ? chains.map((chain: any, chainIndex: number) => <article className="admin-chain" key={chain.id || chainIndex}><header><span>الجولة {details.archiveKind === 'round' ? baseRound : chainIndex + 1}</span><strong>بدأها {chain.originNickname}</strong></header>{(chain.entries || []).map((entry: any, index: number) => <div className={`admin-chain-entry ${entry.kind}`} key={index}><b>{index + 1}</b><div><strong>{entry.authorNickname}</strong><small>{entry.kind === 'drawing' ? 'رسم' : 'نص'}{entry.skipped ? ' · تم التخطي' : ''}</small>{entry.kind === 'text' ? <p>{entry.value}</p> : <p className="muted">🖼️ الرسم محفوظ بصريًا داخل GIF هذه الجولة.</p>}</div></div>)}</article>) : <p className="muted">لا توجد بيانات محفوظة لهذا العنصر.</p>}</div></section>;
}

function AdminBlindDetail({ details }: { details: any }) {
  const scores = Array.isArray(details.scores) ? details.scores : [];
  const awards = Array.isArray(details.awards) ? details.awards : [];
  const drawings = Array.isArray(details.drawings) ? details.drawings : [];
  return (
    <>
      {scores.length > 0 && <section className="admin-detail-section"><h3>الترتيب النهائي</h3><div className="admin-score-grid">{scores.map((score: any, index: number) => <div key={score.playerId || index}><b>#{index + 1}</b><span>{score.nickname}</span><strong>{score.score} نقطة</strong><small>{score.correctGuesses ?? 0} تخمين صحيح</small></div>)}</div></section>}
      {awards.length > 0 && <section className="admin-detail-section"><h3>جوائز الجولة</h3><div className="admin-detail-players">{awards.map((award: any, index: number) => <span key={award.key || index}>{award.emoji || '🏆'} <b>{award.title}</b> · {award.nickname}</span>)}</div></section>}
      <section className="admin-detail-section"><h3>الرسومات</h3><div className="admin-answer-list">{drawings.length ? drawings.map((drawing: any, index: number) => <article className="admin-answer-card" key={drawing.id || index}><header><span>رسمة {index + 1}</span><strong>{drawing.authorNickname}</strong></header><p>{drawing.prompt || drawing.answer || 'مهمة رسم'}</p><div className="admin-answer-winners"><small>الإجابة:</small><span>{drawing.answer || '—'}</span><small> · عرفها {drawing.correctCount ?? drawing.correctGuessers?.length ?? 0}</small></div><p className="muted">🖼️ لا تُخزن صورة الرسم في السجل لتفادي تضخم قاعدة البيانات.</p></article>) : <p className="muted">لم تكتمل رسومات محفوظة في هذه الجلسة.</p>}</div></section>
    </>
  );
}


function AdminMarriageDetail({ details }: { details: any }) {
  const rounds = Array.isArray(details.rounds) ? details.rounds : [];
  const contestants = Array.isArray(details.contestants) ? details.contestants : [];
  const star = details.star || {};
  const winner = details.winner || {};
  return (
    <>
      <section className="admin-detail-section">
        <h3>الاختيار النهائي</h3>
        <div className="admin-marriage-couple">
          <span>👑 {star.nickname || 'العروس/العريس'}</span><b>💍</b><span>🏆 {winner.nickname || 'الفائز'}</span>
        </div>
      </section>
      <section className="admin-detail-section">
        <h3>الجولات والإجابات</h3>
        <div className="admin-answer-list">
          {rounds.length ? rounds.map((round: any, index: number) => (
            <article className="admin-answer-card" key={round.round || index}>
              <header><span>الجولة {round.round || index + 1}</span><strong>{round.question || 'سؤال غير محفوظ'}</strong></header>
              <div className="admin-marriage-answer-list">
                {(round.answers || []).map((answer: any, answerIndex: number) => (
                  <div key={answer.id || answerIndex} className={round.eliminatedPlayerId === answer.playerId ? 'eliminated' : ''}>
                    <strong>{answer.nickname || 'لاعب'}</strong><p>{answer.text || '—'}</p>{round.eliminatedPlayerId === answer.playerId && <small>تم استبعاده في هذه الجولة</small>}
                  </div>
                ))}
              </div>
            </article>
          )) : <p className="muted">لم تكتمل جولات محفوظة.</p>}
        </div>
      </section>
      {contestants.length > 0 && (
        <section className="admin-detail-section">
          <h3>المشاركون النهائيون</h3>
          <div className="admin-detail-players">
            {contestants.map((contestant: any, index: number) => <span key={contestant.playerId || index}>{contestant.isWinner ? '👑' : '🎭'} <b>{contestant.nickname}</b> {contestant.isWinner ? '· الفائز' : `· خرج في الجولة ${contestant.eliminatedRound ?? '—'}`}</span>)}
          </div>
        </section>
      )}
    </>
  );
}

function adminGameLabel(game: AdminGameType) { return game === 'sketch' ? 'SketchRelay' : game === 'who-said' ? 'من قال هذا؟' : game === 'story' ? 'أكمل القصة' : game === 'blind' ? 'ارسم وأنت أعمى' : 'الزواج الأعمى'; }
function adminGameIcon(game: AdminGameType) { return game === 'sketch' ? '🎨' : game === 'who-said' ? '🕵️' : game === 'story' ? '📖' : game === 'blind' ? '🙈' : '💍'; }
function adminStatusLabel(status: AdminHistoryStatus) { return status === 'completed' ? 'مكتملة' : status === 'active' ? 'جارية' : 'أُوقفت'; }
function adminPhaseLabel(phase: string) {
  const labels: Record<string, string> = { lobby: 'في الانتظار', playing: 'جولة نشطة', album_waiting: 'بانتظار الألبوم', reveal: 'عرض النتائج', who_lobby: 'في الانتظار', who_answering: 'استقبال الإجابات', who_ready: 'جاهزة للتخمين', who_guessing: 'مرحلة التخمين', who_results: 'عرض النتائج', story_lobby: 'في الانتظار', story_writing: 'كتابة القصة', story_waiting_reveal: 'بانتظار الكشف', story_reveal: 'عرض القصة', blind_lobby: 'في الانتظار', blind_drawing: 'الرسم الأعمى', blind_guessing: 'تخمين الرسومات', blind_reveal: 'كشف الإجابة', blind_results: 'النتائج والجوائز', marriage_lobby: 'في الانتظار', marriage_proposing: 'التقديم السري', marriage_proposal_closed: 'بانتظار متقدمين', marriage_asking: 'كتابة السؤال', marriage_answering: 'الإجابات السرية', marriage_revealing: 'كشف الإجابات', marriage_eliminating: 'قرار الإقصاء', marriage_elimination_reveal: 'عرض الاستبعاد', marriage_finale: 'الكشف النهائي' };
  return labels[phase] || phase;
}
function formatAdminDate(ms: number) { return new Intl.DateTimeFormat('ar-AT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(ms)); }
function formatAdminTime(ms: number) { return new Intl.DateTimeFormat('ar-AT', { hour: '2-digit', minute: '2-digit' }).format(new Date(ms)); }
function formatAdminDateTime(ms: number) { return `${formatAdminDate(ms)} · ${formatAdminTime(ms)}`; }
function formatAdminDuration(ms: number | null) {
  if (ms == null) return '—';
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  if (minutes >= 60) return `${Math.floor(minutes / 60)}س ${minutes % 60}د`;
  return minutes ? `${minutes}د ${seconds}ث` : `${seconds}ث`;
}

function PartyHome({ onChoose }: { onChoose: (mode: GameMode) => void }) {
  return (
    <main className="party-home page-wrap">
      <section className="party-hero">
        <div className="party-logo-wrap">
          <span className="brand-mark company-logo-mark party-company-logo"><img src="/brand/mbb-logo-mark.png" alt="MBB Websolutions" /></span>
          <div><span className="eyebrow">MBB WEBSOLUTIONS</span><h1>MBB Party Games</h1></div>
        </div>
        <p>اختر لعبة، اجمع أصدقاءك، وابدأ الفوضى. جميع الألعاب مباشرة من المتصفح وتعمل على الهاتف والكمبيوتر.</p>
      </section>

      <div className="party-admin-link-row"><a className="party-admin-link" href="/admin">🔒 لوحة الإدارة</a></div>

      <section className="party-game-grid">
        <button type="button" className="party-game-card sketch-card" onClick={() => onChoose('sketch')}>
          <div className="party-game-art"><span>✎</span><i>🎨</i><b>💬</b></div>
          <div className="party-game-copy"><span className="party-game-tag">رسم + تخمين</span><h2>SketchRelay</h2><p>اكتب جملة، دع شخصًا يرسمها، ثم شاهد كيف تتحول إلى كارثة فنية مضحكة.</p><strong>دخول اللعبة ←</strong></div>
        </button>

        <button type="button" className="party-game-card who-card" onClick={() => onChoose('who-said')}>
          <div className="party-game-art who-art"><span>?</span><i>🤫</i><b>🕵️</b></div>
          <div className="party-game-copy"><span className="party-game-tag">أسرار + تصويت</span><h2>من قال هذا؟</h2><p>الجميع يجيب سرًا، ثم حاول اكتشاف صاحب كل إجابة قبل أن تنكشف الحقيقة في النهاية.</p><strong>دخول اللعبة ←</strong></div>
        </button>


        <button type="button" className="party-game-card story-card" onClick={() => onChoose('story')}>
          <div className="party-game-art story-art"><span>✦</span><i>📖</i><b>✍️</b></div>
          <div className="party-game-copy"><span className="party-game-tag story-tag">قصة + مفاجآت</span><h2>أكمل القصة</h2><p>ترى فقط آخر جزء مما كتبه اللاعب قبلك، وتكمل القصة بطريقتك. القصة الكاملة لا تظهر إلا عندما يكشفها الأدمن.</p><strong>دخول اللعبة ←</strong></div>
        </button>

        <button type="button" className="party-game-card blind-card" onClick={() => onChoose('blind')}>
          <div className="party-game-art blind-art"><span>🙈</span><i>✎</i><b>💥</b></div>
          <div className="party-game-copy"><span className="party-game-tag blind-tag">رسم أعمى + تخمين</span><h2>ارسم وأنت أعمى</h2><p>احفظ الكلمة، ابدأ الرسم، وبعد لحظة تختفي خطوطك. أكمل بثقة ثم شاهد أصدقاءك يحاولون فهم الكارثة.</p><strong>دخول اللعبة ←</strong></div>
        </button>

        <button type="button" className="party-game-card marriage-card" onClick={() => onChoose('marriage')}>
          <div className="party-game-art marriage-art"><span>💍</span><i>♥</i><b>🎭</b></div>
          <div className="party-game-copy"><span className="party-game-tag marriage-tag">اختيار سري + إقصاء</span><h2>الزواج الأعمى</h2><p>متقدمون مجهولون، أسئلة علنية، إجابات بلا أسماء، وإقصاء بعد كل جولة حتى يبقى الاختيار الأخير.</p><strong>دخول اللعبة ←</strong></div>
        </button>
      </section>
    </main>
  );
}

function PlayerStrip({ room, self, notify }: { room: PartyRoomState; self: Player; notify: (message: string) => void }) {
  const submitted = new Set('submittedPlayerIds' in room ? (room.submittedPlayerIds ?? []) : []);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const kick = async (player: Player) => {
    if (!self.isChef || player.id === self.id) return;
    if (!window.confirm(`طرد ${player.nickname} من اللعبة؟ إذا كانت لديه مهمة حالية فسيتم تخطيها ولن تُنقل إلى لاعب آخر.`)) return;
    try {
      const result = await emitAck('chef:kick', { targetId: player.id });
      if (!result.ok) throw new Error(result.error);
      setSelectedId(null);
      notify(`تم طرد ${player.nickname}.`);
    } catch (error) {
      notify(messageOf(error));
    }
  };

  return (
    <div className="player-strip-wrap">
      <div className="player-strip" aria-label="اللاعبون في الغرفة">
        {room.players.map((player, index) => {
          const canKick = self.isChef && player.id !== self.id;
          const selected = selectedId === player.id;
          return (
            <div
              key={player.id}
              className={`player-chip ${player.id === self.id ? 'me' : ''} ${player.isSpectator ? 'spectator' : ''} ${!player.connected ? 'offline' : ''} ${canKick ? 'chef-clickable' : ''} ${selected ? 'selected' : ''}`}
              title={canKick ? `اضغط لإدارة ${player.nickname}` : player.connected ? player.nickname : `${player.nickname} - منقطع`}
              onClick={() => canKick && setSelectedId((current) => current === player.id ? null : player.id)}
            >
              <FunnyAvatar name={player.nickname} className="mini-avatar" style={{ '--avatar-index': index } as CSSProperties} />
              <span className="player-chip-name">{player.isChef ? '👑 ' : player.isSpectator ? '👁️ ' : ''}{player.nickname}</span>
              {['playing', 'who_answering', 'who_guessing', 'story_writing', 'blind_drawing'].includes(room.phase) && !player.isSpectator && <i className={submitted.has(player.id) ? 'done' : ''}>{submitted.has(player.id) ? '✓' : '•'}</i>}
              {selected && canKick && (
                <button
                  type="button"
                  className="strip-kick-action"
                  onClick={(event) => { event.stopPropagation(); void kick(player); }}
                >
                  طرد
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChefLiveControls({ room, notify }: { room: RoomState; notify: (message: string) => void }) {
  const phase = room.phase;
  const [busy, setBusy] = useState<'advance' | 'end' | 'restart' | null>(null);

  const forceAdvance = async () => {
    if (!window.confirm('إنهاء انتظار هذه الجولة الآن والانتقال للمرحلة التالية؟ اللاعبون الذين لم يرسلوا بعد سيُسجل لهم انتهاء الوقت.')) return;
    setBusy('advance');
    try {
      const result = await emitAck('chef:next', { gameId: room.sessionId, turn: room.turn });
      if (!result.ok) throw new Error(result.error);
      notify('تم إنهاء انتظار الجولة والانتقال مباشرة.');
    } catch (error) {
      notify(messageOf(error));
    } finally {
      setBusy(null);
    }
  };

  const endGame = async () => {
    if (!window.confirm('إنهاء اللعبة الحالية وإعادة الجميع إلى غرفة الانتظار؟')) return;
    setBusy('end');
    try {
      const result = await emitAck('chef:reset', {});
      if (!result.ok) throw new Error(result.error);
      notify('تم إنهاء اللعبة وإعادة الغرفة إلى وضع الانتظار.');
    } catch (error) {
      notify(messageOf(error));
    } finally {
      setBusy(null);
    }
  };

  const restartGame = async () => {
    if (!window.confirm('سيتم إنهاء الحالة الحالية وبدء لعبة جديدة فورًا باللاعبين المتصلين. متابعة؟')) return;
    setBusy('restart');
    try {
      const result = await emitAck('chef:start', {});
      if (!result.ok) throw new Error(result.error);
      notify('بدأت لعبة جديدة.');
    } catch (error) {
      notify(messageOf(error));
    } finally {
      setBusy(null);
    }
  };

  const phaseText = phase === 'playing'
    ? 'اللعبة قيد التشغيل الآن. يمكنك إنهاء انتظار الجولة الحالية فورًا عند تأخر أي لاعب.'
    : phase === 'album_waiting'
      ? 'انتهت الجولات والألبوم ينتظر العرض.'
      : 'عرض الألبوم جارٍ الآن.';

  return (
    <section className="glass-card chef-live-bar">
      <div>
        <strong>👑 تحكم الأدمن المباشر</strong>
        <small className="muted">{phaseText}</small>
      </div>
      <div className="chef-live-actions">
        {phase === 'playing' && (
          <button type="button" className="primary-btn admin-force-advance" onClick={forceAdvance} disabled={busy !== null}>
            {busy === 'advance' ? 'جاري الانتقال...' : 'إنهاء انتظار الجولة الآن ⏭'}
          </button>
        )}
        <button type="button" className="secondary-btn" onClick={endGame} disabled={busy !== null}>
          {busy === 'end' ? 'جاري الإنهاء...' : 'إنهاء اللعبة'}
        </button>
        <button type="button" className="secondary-btn" onClick={restartGame} disabled={busy !== null}>
          {busy === 'restart' ? 'جاري البدء...' : 'بدء لعبة جديدة ↻'}
        </button>
      </div>
    </section>
  );
}

function JoinScreen({ connected, mode, phase, onJoin }: { connected: boolean; mode: GameMode; phase?: PartyRoomState['phase']; onJoin: (nickname: string, chefPassword: string, options?: { gender?: MarriageGender; spectator?: boolean }) => Promise<void> }) {
  const who = mode === 'who-said';
  const story = mode === 'story';
  const blind = mode === 'blind';
  const marriage = mode === 'marriage';
  const viewerMode = mode === 'sketch'
    ? phase === 'album_waiting' || phase === 'reveal'
    : who
      ? Boolean(phase && phase !== 'who_lobby')
      : story
        ? Boolean(phase && phase !== 'story_lobby')
        : blind
          ? Boolean(phase && phase !== 'blind_lobby')
          : Boolean(phase && phase !== 'marriage_lobby');
  const [nickname, setNickname] = useState('');
  const [chefMode, setChefMode] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [gender, setGender] = useState<MarriageGender>('male');
  const [spectator, setSpectator] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (nickname.trim().length < 2) {
      setError('اكتب اسمًا مستعارًا من حرفين على الأقل.');
      return;
    }
    setBusy(true);
    try {
      await onJoin(nickname.trim(), chefMode ? password : '', marriage ? { gender, spectator: viewerMode ? true : spectator } : undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'تعذر الدخول.');
    } finally {
      setBusy(false);
    }
  };


  return (
    <section className="join-layout">
      <div className="hero-copy">
        <span className="eyebrow">{viewerMode ? 'المشاهدة مفتوحة للجميع' : who ? 'لعبة أسرار وتخمين' : story ? 'لعبة كتابة جماعية' : blind ? 'رسم أعمى + تخمين' : marriage ? 'اختيار سري + إقصاء' : 'لعبة جماعية مباشرة'}</span>
        {who ? (
          <>
            <h1>كل إجابة سرية.<br /><span>لكن من قالها؟</span></h1>
            <p>يطرح الأدمن سؤالًا، يجيب الجميع في السر، ثم تبدأ التحقيقات: اختر صاحب كل إجابة واكتشف الحقيقة في النهاية.</p>
            <div className="feature-chips"><span>🤫 إجابات سرية</span><span>🕵️ تخمين جماعي</span><span>🏆 نقاط ونتائج</span></div>
          </>
        ) : story ? (
          <>
            <h1>كل لاعب يرى النهاية فقط.<br /><span>والقصة تخرج عن السيطرة.</span></h1>
            <p>يبدأ الأدمن بجملة، ثم يكمل كل لاعب وهو لا يرى إلا آخر جملة من الذي سبقه. القصة الكاملة تبقى مخفية حتى لحظة الكشف.</p>
            <div className="feature-chips"><span>📖 قصة مخفية</span><span>✍️ دور لكل لاعب</span><span>🎭 كشف نهائي</span></div>
          </>
        ) : marriage ? (
          <>
            <h1>لا أسماء. لا تلميحات.<br /><span>فقط الإجابات تقرر.</span></h1>
            <p>يتقدم اللاعبون سرًا، يطرح العريس أو العروس الأسئلة، وتظهر الإجابات بدون أسماء. في كل جولة يخرج شخص واحد حتى لحظة الكشف.</p>
            <div className="feature-chips"><span>💌 تقديم سري</span><span>🎭 إجابات مجهولة</span><span>💍 كشف نهائي</span></div>
          </>
        ) : blind ? (
          <>
            <h1>ارسمها… ثم<br /><span>انسَ أنك رأيتها.</span></h1>
            <p>الجميع يرسم في الوقت نفسه. بعد ثوانٍ تختفي خطوطك بينما يستمر القلم بالعمل، ثم يحاول الأصدقاء تخمين ما كنت تقصده.</p>
            <div className="feature-chips"><span>🙈 رسم بدون رؤية</span><span>🎯 تخمين سريع</span><span>🏆 جوائز مضحكة</span></div>
          </>
        ) : (
          <>
            <h1>من جملة بسيطة<br /><span>إلى كارثة فنية.</span></h1>
            <p>اكتب فكرة، دع صديقك يرسمها، ثم شاهد كيف تتحول القصة مع كل لاعب.</p>
            <div className="feature-chips"><span>✦ بدون حسابات</span><span>✦ يعمل على الهاتف</span><span>✦ ألبوم حي</span></div>
          </>
        )}
      </div>

      <form className="glass-card join-card" onSubmit={submit}>
        <div className="card-icon">{who ? '🕵️' : story ? '📖' : blind ? '🙈' : marriage ? '💍' : '✎'}</div>
        <h2>{viewerMode ? 'ادخل للمشاهدة' : 'ادخل إلى اللعبة'}</h2>
        <p className="muted">{viewerMode ? 'الجولة بدأت بالفعل، لذلك ستدخل كمشاهد حتى تبدأ لعبة جديدة.' : 'كل ما تحتاجه هو اسم مستعار.'}</p>
        <label className="field-label">الاسم المستعار</label>
        <input className="input" value={nickname} maxLength={24} autoComplete="nickname" placeholder="مثال: Maher" onChange={(e) => setNickname(e.target.value)} />
        {marriage && !chefMode && (
          <div className="marriage-join-options">
            <div className="marriage-gender-choice"><span>الجنس</span><div><button type="button" className={gender === 'male' ? 'active' : ''} onClick={() => setGender('male')}>🤵 ذكر</button><button type="button" className={gender === 'female' ? 'active' : ''} onClick={() => setGender('female')}>👰 أنثى</button></div></div>
            {!viewerMode && <button type="button" className={`marriage-spectator-toggle ${spectator ? 'active' : ''}`} onClick={() => setSpectator((value) => !value)}><span>👁️</span><div><strong>الدخول كمتفرج فقط</strong><small>لن أشارك في التقديم أو الإجابات</small></div><i>{spectator ? '✓' : '+'}</i></button>}
          </div>
        )}
        <button type="button" className={`chef-toggle ${chefMode ? 'active' : ''}`} onClick={() => setChefMode((v) => !v)}>
          <span className="chef-toggle-icon">👑</span><span><strong>الدخول كأدمن</strong><small>لدي كلمة سر الإدارة</small></span><i>{chefMode ? '✓' : '+'}</i>
        </button>
        {chefMode && (
          <div className="slide-field">
            <label className="field-label">كلمة سر الأدمن</label>
            <input className="input" type="password" value={password} autoComplete="current-password" placeholder="••••••••" onChange={(e) => setPassword(e.target.value)} />
          </div>
        )}
        {error && <div className="error-box">{error}</div>}
        <button className="primary-btn xl full" disabled={busy || !connected}>{busy ? 'جاري الدخول...' : connected ? (viewerMode ? 'دخول للمشاهدة' : 'دخول اللعبة') : 'جاري الاتصال...'}<span>←</span></button>
      </form>
    </section>
  );
}


function StoryRouter({ self, room, task, reveal, notify }: { self: Player; room: StoryState; task: StoryTask | null; reveal: StoryRevealPayload | null; notify: (message: string) => void }) {
  if (room.phase === 'story_lobby') return <StoryLobby self={self} room={room} notify={notify} />;
  if (room.phase === 'story_writing') return <StoryWritingScreen self={self} room={room} task={task?.kind === 'story-write' ? task : null} notify={notify} />;
  if (room.phase === 'story_waiting_reveal') return <StoryWaitingRevealScreen self={self} room={room} notify={notify} />;
  if (room.phase === 'story_reveal') return <StoryRevealScreen self={self} room={room} reveal={reveal} notify={notify} />;
  return <Splash />;
}

function StoryChefLiveControls({ room, notify }: { room: StoryState; notify: (message: string) => void }) {
  const [busy, setBusy] = useState<'skip' | 'reveal' | 'reset' | null>(null);

  const reset = async () => {
    if (!window.confirm('إنهاء القصة الحالية والعودة إلى غرفة الانتظار؟')) return;
    setBusy('reset');
    try {
      const result = await emitAck('chef:storyReset', {});
      if (!result.ok) throw new Error(result.error);
      notify('تم إنهاء القصة والعودة إلى غرفة الانتظار.');
    } catch (error) { notify(messageOf(error)); }
    finally { setBusy(null); }
  };

  const skip = async () => {
    if (!window.confirm(`تخطي دور ${room.currentPlayerNickname || 'اللاعب الحالي'}؟`)) return;
    setBusy('skip');
    try {
      const result = await emitAck('chef:storySkip', { turnIndex: room.turnIndex, currentPlayerId: room.currentPlayerId });
      if (!result.ok) throw new Error(result.error);
      notify('تم تخطي الدور والانتقال إلى اللاعب التالي.');
    } catch (error) { notify(messageOf(error)); }
    finally { setBusy(null); }
  };

  const reveal = async () => {
    setBusy('reveal');
    try {
      const result = await emitAck('chef:storyReveal', {});
      if (!result.ok) throw new Error(result.error);
    } catch (error) { notify(messageOf(error)); setBusy(null); }
  };

  return (
    <section className="glass-card chef-live-bar story-live-bar">
      <div>
        <strong>👑 تحكم الأدمن</strong>
        <small className="muted">{room.phase === 'story_writing' ? `الدور الآن لـ ${room.currentPlayerNickname || 'لاعب'}.` : 'انتهت الكتابة والقصة ما زالت مخفية.'}</small>
      </div>
      <div className="chef-live-actions">
        {room.phase === 'story_writing' && <button type="button" className="secondary-btn" onClick={skip} disabled={busy !== null}>{busy === 'skip' ? 'جاري التخطي...' : 'إنهاء وقت الدور الآن ⏭'}</button>}
        {room.phase === 'story_waiting_reveal' && <button type="button" className="primary-btn" onClick={reveal} disabled={busy !== null}>{busy === 'reveal' ? 'جاري الكشف...' : 'عرض القصة كاملة 📖'}</button>}
        <button type="button" className="secondary-btn" onClick={reset} disabled={busy !== null}>{busy === 'reset' ? 'جاري الإنهاء...' : 'إنهاء اللعبة'}</button>
      </div>
    </section>
  );
}

const STORY_STARTER_SUGGESTIONS = [
  'استيقظت هذا الصباح ووجدت بقرة جالسة في المطبخ وكأنها تنتظر القهوة...',
  'رن جرس الباب في منتصف الليل، وعندما فتحته وجدت صندوقًا مكتوبًا عليه: لا تفتحه أبدًا...',
  'في أول يوم لي في العمل اكتشفت أن المدير ليس إنسانًا عاديًا...',
  'كنا في الطريق إلى حفلة هادئة، لكن السائق انعطف فجأة نحو غابة لا تظهر على الخريطة...'
];

function StoryLobby({ self, room, notify }: { self: Player; room: StoryState; notify: (message: string) => void }) {
  const [settings, setSettings] = useState(room.storySettings);
  const [starter, setStarter] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => setSettings(room.storySettings), [room.storySettings]);

  const connectedPlayers = room.players.filter((p) => p.connected && !p.isSpectator);
  const continuers = connectedPlayers.filter((p) => !p.isChef);
  const canStart = connectedPlayers.length >= room.minPlayers && continuers.length >= 1 && starter.trim().length >= 4;

  const patch = <K extends keyof StoryState['storySettings']>(key: K, value: StoryState['storySettings'][K]) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const start = async () => {
    setBusy(true);
    try {
      const save = await emitAck('chef:storySettings', settings);
      if (!save.ok) throw new Error(save.error);
      const result = await emitAck('chef:storyStart', { starter: starter.trim() });
      if (!result.ok) throw new Error(result.error);
    } catch (error) {
      notify(messageOf(error));
      setBusy(false);
    }
  };

  const kick = async (targetId: string) => {
    try {
      const result = await emitAck('chef:kick', { targetId });
      if (!result.ok) throw new Error(result.error);
      notify('تم إخراج اللاعب من الغرفة.');
    } catch (error) { notify(messageOf(error)); }
  };

  return (
    <section className="lobby-layout story-lobby">
      <div className="section-heading">
        <div><span className="eyebrow">غرفة أكمل القصة</span><h1>اكتب البداية... واترك الباقي للفوضى.</h1><p>الأدمن يكتب افتتاحية القصة. كل لاعب بعده يرى فقط آخر جملة من المساهمة السابقة، وليس القصة الكاملة.</p></div>
        <div className="room-stat"><strong>{connectedPlayers.length}</strong><span>متصل</span></div>
      </div>

      <div className={`lobby-grid ${self.isChef ? 'with-controls' : ''}`}>
        <div className="glass-card players-card">
          <div className="card-header-row"><div><h2>كتّاب القصة</h2><p className="muted">الأدمن يضع البداية، ثم يكتب اللاعبون بالتتابع</p></div><span className="live-badge"><i /> مباشر</span></div>
          <div className="players-grid">
            {room.players.map((player, index) => (
              <div className={`player-card ${!player.connected ? 'disconnected' : ''}`} key={player.id}>
                <FunnyAvatar as="div" name={player.nickname} className="avatar" style={{ '--avatar-index': index } as CSSProperties} />
                <div className="player-meta"><strong>{player.nickname}</strong><small>{player.isChef ? '👑 يبدأ القصة ويدير الجولة' : player.connected ? 'جاهز للكتابة' : 'منقطع مؤقتًا'}</small></div>
                {self.isChef && player.id !== self.id ? <button type="button" className="kick-btn" title="إخراج اللاعب" onClick={() => kick(player.id)}>×</button> : <span className={`presence ${player.connected ? 'on' : ''}`} />}
              </div>
            ))}
          </div>
        </div>

        {self.isChef ? (
          <aside className="glass-card chef-panel story-chef-panel">
            <span className="chef-label">👑 لوحة الأدمن</span>
            <h2>بداية القصة</h2>
            <p className="muted">هذه هي الجملة الوحيدة التي تبدأ منها السلسلة. اللاعب الأول سيرى نهايتها، ثم تنتقل القصة لاعبًا بعد لاعب.</p>
            <textarea className="input textarea story-starter-input" maxLength={500} value={starter} onChange={(e) => setStarter(e.target.value)} placeholder="مثال: استيقظت ووجدت بقرة في المطبخ..." />
            <div className="story-suggestions">
              {STORY_STARTER_SUGGESTIONS.map((item, index) => <button type="button" key={index} onClick={() => setStarter(item)}>{index + 1}</button>)}
            </div>

            <div className="setting-divider">إعدادات الجولة</div>
            <TimeSetting label="وقت كتابة كل لاعب" value={settings.lineSeconds} min={20} max={300} onChange={(value) => patch('lineSeconds', value)} />
            <div className="story-context-setting">
              <div><strong>نسبة الجزء الظاهر من كل مساهمة</strong><span>{settings.contextPercent}%</span></div>
              <input type="range" min={10} max={50} step={5} value={settings.contextPercent} onChange={(e) => patch('contextPercent', Number(e.target.value))} />
              <small>كل لاعب يرى فقط آخر {settings.contextPercent}% تقريبًا من مساهمة اللاعب السابق، مع الحفاظ على الكلمات كاملة. لا تُعرض أي مساهمة كاملة إلا إذا كانت قصيرة جدًا لدرجة أنها كلمة واحدة.</small>
            </div>

            <button type="button" className="primary-btn xl full" onClick={start} disabled={busy || !canStart}>
              {busy ? 'جاري بدء القصة...' : canStart ? 'ابدأ القصة وأرسل الدور الأول ←' : starter.trim().length < 4 ? 'اكتب بداية القصة أولًا' : 'نحتاج لاعبًا واحدًا على الأقل مع الأدمن'}
            </button>
          </aside>
        ) : (
          <aside className="glass-card waiting-panel"><div className="waiting-orbit"><span>📖</span></div><h2>بانتظار بداية القصة</h2><p>عندما يبدأ الأدمن، سيأتي دور كل لاعب على حدة. لن ترى إلا الجزء المسموح من آخر مساهمة.</p></aside>
        )}
      </div>
    </section>
  );
}

function StoryWritingScreen({ self, room, task, notify }: { self: Player; room: StoryState; task: Extract<StoryTask, { kind: 'story-write' }> | null; notify: (message: string) => void }) {
  const [line, setLine] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setLine(''); setBusy(false); }, [task?.turn]);

  const progress = Math.round((room.completedTurns / Math.max(1, room.totalTurns)) * 100);
  const current = room.players.find((p) => p.id === room.currentPlayerId);

  if (self.isSpectator) {
    return <StoryWaitingCard room={room} icon="👁️" title="القصة تُكتب الآن" text="أنت في وضع المشاهدة. القصة الكاملة لن تظهر لأي شخص قبل أن يضغط الأدمن على زر الكشف." />;
  }

  if (!task) {
    return (
      <section className="story-wait-stage game-layout">
        <div className="game-header"><div className="round-info"><span>تقدم القصة · {room.completedTurns} من {room.totalTurns}</span><div className="round-track"><i style={{ width: `${progress}%` }} /></div></div>{room.endsAt && <Countdown endsAt={room.endsAt} />}</div>
        <div className="glass-card story-turn-wait">
          <span className="story-wait-icon">✍️</span>
          <span className="eyebrow">ليس دورك بعد</span>
          <h1>الدور الآن لـ {room.currentPlayerNickname || 'أحد اللاعبين'}</h1>
          {current && <FunnyAvatar name={current.nickname} className="story-current-avatar" />}
          <p>لن نعرض لك ما يكتبه. عندما يأتي دورك سترى فقط آخر {room.storySettings.contextPercent}% تقريبًا من مساهمة اللاعب السابق.</p>
          <div className="waiting-pulse"><i /><span>القصة مستمرة بسرية...</span></div>
        </div>
      </section>
    );
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!line.trim() || busy) return;
    setBusy(true);
    try {
      const result = await emitAck('story:submitLine', { value: line.trim() });
      if (!result.ok) throw new Error(result.error);
      setLine('');
    } catch (error) {
      notify(messageOf(error));
      setBusy(false);
    }
  };

  return (
    <section className="story-write-stage game-layout">
      <div className="game-header">
        <div className="round-info"><span>دورك الآن · {task.turn} من {task.total}</span><div className="round-track"><i style={{ width: `${Math.round(((task.turn - 1) / Math.max(1, task.total)) * 100)}%` }} /></div></div>
        <Countdown endsAt={task.endsAt} />
      </div>

      <div className="glass-card story-context-card">
        <span className="story-context-badge">هذا فقط ما يُسمح لك برؤيته</span>
        <blockquote>“{task.context}”</blockquote>
        <p>أكمل من هنا. لا تعرف ما حدث قبل هذا الجزء، واللاعب التالي لن يرى إلا آخر {room.storySettings.contextPercent}% تقريبًا مما ستكتبه أنت.</p>
      </div>

      <form className="glass-card story-write-form" onSubmit={submit}>
        <label className="field-label">تكملتك للقصة</label>
        <textarea className="input textarea" value={line} maxLength={500} autoFocus placeholder="اكتب السطر التالي بطريقة منطقية... أو كارثية 😈" onChange={(e) => setLine(e.target.value)} />
        <div className="input-footer"><span>{line.length}/500</span><small>🔒 القصة الكاملة مخفية حتى النهاية</small></div>
        <button className="primary-btn xl full" disabled={busy || !line.trim()}>{busy ? 'جاري تمرير القصة...' : 'إرسال وتمريرها للاعب التالي →'}</button>
      </form>
    </section>
  );
}

function StoryWaitingRevealScreen({ self, room, notify }: { self: Player; room: StoryState; notify: (message: string) => void }) {
  const [busy, setBusy] = useState(false);
  const reveal = async () => {
    setBusy(true);
    try {
      const result = await emitAck('chef:storyReveal', {});
      if (!result.ok) throw new Error(result.error);
    } catch (error) { notify(messageOf(error)); setBusy(false); }
  };
  return (
    <section className="glass-card story-reveal-ready">
      <div className="story-ready-book">📕</div>
      <span className="eyebrow">القصة اكتملت</span>
      <h1>لكن لا أحد يعرف ماذا كتب الجميع.</h1>
      <p>اكتملت {room.completedTurns} أدوار. القصة الكاملة ما زالت مخفية حتى يقرر الأدمن عرضها للجميع في نفس اللحظة.</p>
      {self.isChef ? <button type="button" className="primary-btn xl" onClick={reveal} disabled={busy}>{busy ? 'جاري فتح الكتاب...' : 'عرض القصة كاملة الآن 📖'}</button> : <div className="waiting-pulse"><i /><span>بانتظار الأدمن لكشف القصة...</span></div>}
    </section>
  );
}

function StoryRevealScreen({ self, room, reveal, notify }: { self: Player; room: StoryState; reveal: StoryRevealPayload | null; notify: (message: string) => void }) {
  const [busy, setBusy] = useState(false);
  const reset = async () => {
    setBusy(true);
    try {
      const result = await emitAck('chef:storyReset', {});
      if (!result.ok) throw new Error(result.error);
    } catch (error) { notify(messageOf(error)); setBusy(false); }
  };

  if (!reveal) return <section className="center-state glass-card"><div className="spinner" /><h2>فتح القصة الكاملة...</h2></section>;
  const chef = room.players.find((p) => p.isChef);

  return (
    <section className="story-reveal-stage">
      <div className="story-reveal-heading"><span className="eyebrow">لحظة الحقيقة</span><h1>القصة الكاملة 📖</h1><p>الآن فقط يرى الجميع كيف تحولت بداية بسيطة إلى قصة كتبها أشخاص لم يعرفوا إلا آخر سطر.</p></div>

      <article className="glass-card story-book">
        <div className="story-book-top"><span>THE END?</span><b>✦</b></div>
        <div className="story-full-text">{reveal.fullStory}</div>
      </article>

      <section className="glass-card story-breakdown">
        <div className="card-header-row"><div><h2>كيف كُتبت القصة؟</h2><p className="muted">المساهمات بالترتيب الحقيقي</p></div></div>
        <div className="story-entry-list">
          <div className="story-entry starter-entry">
            <span className="story-entry-number">0</span>
            {chef ? <FunnyAvatar name={chef.nickname} className="story-entry-avatar" /> : <span className="story-entry-avatar story-admin-fallback">👑</span>}
            <div><strong>{chef?.nickname || 'الأدمن'} <small>بداية القصة</small></strong><p>{reveal.starter}</p></div>
          </div>
          {reveal.contributions.map((entry, index) => (
            <div className="story-entry" key={entry.id}>
              <span className="story-entry-number">{index + 1}</span>
              <FunnyAvatar name={entry.authorNickname} className="story-entry-avatar" style={{ '--avatar-index': index } as CSSProperties} />
              <div><strong>{entry.authorNickname}</strong><p>{entry.text}</p></div>
            </div>
          ))}
        </div>
      </section>

      {self.isChef && <div className="who-results-actions"><button className="primary-btn xl" onClick={reset} disabled={busy}>{busy ? 'جاري التجهيز...' : 'قصة جديدة ↻'}</button></div>}
    </section>
  );
}

function StoryWaitingCard({ room, icon, title, text }: { room: StoryState; icon: string; title: string; text: string }) {
  return (
    <section className="glass-card story-turn-wait"><span className="story-wait-icon">{icon}</span><span className="eyebrow">وضع المشاهدة</span><h1>{title}</h1><p>{text}</p><div className="waiting-pulse"><i /><span>{room.completedTurns} من {room.totalTurns} أدوار انتهت</span></div></section>
  );
}

function WhoSaidRouter({ self, room, task, results, notify }: { self: Player; room: WhoSaidState; task: WhoSaidTask | null; results: WhoSaidResultsPayload | null; notify: (message: string) => void }) {
  if (room.phase === 'who_lobby') return <WhoSaidLobby self={self} room={room} notify={notify} />;
  if (room.phase === 'who_answering') return <WhoAnswerScreen self={self} room={room} task={task?.kind === 'who-answer' ? task : null} notify={notify} />;
  if (room.phase === 'who_ready') return <WhoReadyScreen self={self} room={room} notify={notify} />;
  if (room.phase === 'who_guessing') return <WhoGuessScreen self={self} room={room} task={task?.kind === 'who-guess' ? task : null} notify={notify} />;
  if (room.phase === 'who_results') return <WhoResultsScreen self={self} room={room} payload={results} notify={notify} />;
  return <Splash />;
}

function WhoChefLiveControls({ room, notify }: { room: WhoSaidState; notify: (message: string) => void }) {
  const [busy, setBusy] = useState<'advance' | 'reset' | null>(null);

  const advanceLabel = room.phase === 'who_answering'
    ? 'إنهاء انتظار الإجابات الآن ⏭'
    : room.phase === 'who_ready'
      ? 'بدء التخمين الآن ⏭'
      : 'إنهاء التخمين الحالي الآن ⏭';

  const forceAdvance = async () => {
    if (!window.confirm('هل تريد إنهاء وقت هذه المرحلة الآن والانتقال مباشرة؟')) return;
    setBusy('advance');
    try {
      const result = await emitAck('chef:whoAdvance', { phase: room.phase, guessIndex: room.guessIndex });
      if (!result.ok) throw new Error(result.error);
      notify('تم إنهاء انتظار المرحلة والانتقال مباشرة.');
    } catch (error) {
      notify(messageOf(error));
    } finally {
      setBusy(null);
    }
  };

  const reset = async () => {
    if (!window.confirm('إنهاء اللعبة الحالية والعودة إلى غرفة الانتظار؟')) return;
    setBusy('reset');
    try {
      const result = await emitAck('chef:whoReset', {});
      if (!result.ok) throw new Error(result.error);
      notify('تم إنهاء اللعبة والعودة إلى غرفة الانتظار.');
    } catch (error) {
      notify(messageOf(error));
    } finally {
      setBusy(null);
    }
  };
  return (
    <section className="glass-card chef-live-bar who-live-bar">
      <div><strong>👑 تحكم الأدمن</strong><small className="muted">يمكنك تجاوز انتظار اللاعبين أو إنهاء اللعبة بالكامل.</small></div>
      <div className="chef-live-actions">
        <button type="button" className="primary-btn admin-force-advance" onClick={forceAdvance} disabled={busy !== null}>{busy === 'advance' ? 'جاري الانتقال...' : advanceLabel}</button>
        <button type="button" className="secondary-btn" onClick={reset} disabled={busy !== null}>{busy === 'reset' ? 'جاري الإنهاء...' : 'إنهاء اللعبة'}</button>
      </div>
    </section>
  );
}

const WHO_QUESTION_SUGGESTIONS = [
  'ما أسوأ شيء يمكن أن تفعله في أول موعد؟',
  'ما أغرب كذبة قلتها وصدقها شخص آخر؟',
  'لو أصبحت مشهورًا غدًا، ما السبب الأكثر إحراجًا لشهرتك؟',
  'ما الشيء الذي قد تشتريه لو ربحت مليون يورو ولن تعترف به عادة؟'
];

function WhoSaidLobby({ self, room, notify }: { self: Player; room: WhoSaidState; notify: (message: string) => void }) {
  const [settings, setSettings] = useState(room.whoSettings);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => setSettings(room.whoSettings), [room.whoSettings]);

  const connectedPlayers = room.players.filter((p) => p.connected && !p.isSpectator);
  const canStart = connectedPlayers.length >= room.minPlayers && question.trim().length >= 4;

  const patch = <K extends keyof WhoSaidState['whoSettings']>(key: K, value: WhoSaidState['whoSettings'][K]) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const start = async () => {
    setBusy(true);
    try {
      const save = await emitAck('chef:whoSettings', settings);
      if (!save.ok) throw new Error(save.error);
      const result = await emitAck('chef:whoStart', { question: question.trim() });
      if (!result.ok) throw new Error(result.error);
    } catch (error) {
      notify(messageOf(error));
      setBusy(false);
    }
  };

  const kick = async (targetId: string) => {
    try {
      const result = await emitAck('chef:kick', { targetId });
      if (!result.ok) throw new Error(result.error);
      notify('تم إخراج اللاعب من الغرفة.');
    } catch (error) { notify(messageOf(error)); }
  };

  return (
    <section className="lobby-layout who-lobby">
      <div className="section-heading">
        <div><span className="eyebrow">غرفة من قال هذا؟</span><h1>جهّز السؤال... وخليهم يتورطوا.</h1><p>كل لاعب سيجيب سرًا. لن تظهر هوية أي إجابة إلا بعد انتهاء جميع التخمينات.</p></div>
        <div className="room-stat"><strong>{connectedPlayers.length}</strong><span>لاعب متصل</span></div>
      </div>

      <div className={`lobby-grid ${self.isChef ? 'with-controls' : ''}`}>
        <div className="glass-card players-card">
          <div className="card-header-row"><div><h2>المحققون</h2><p className="muted">نحتاج {room.minPlayers} لاعبين على الأقل</p></div><span className="live-badge"><i /> مباشر</span></div>
          <div className="players-grid">
            {room.players.map((player, index) => (
              <div className={`player-card ${!player.connected ? 'disconnected' : ''}`} key={player.id}>
                <FunnyAvatar as="div" name={player.nickname} className="avatar" style={{ '--avatar-index': index } as CSSProperties} />
                <div className="player-meta"><strong>{player.nickname}</strong><small>{player.isChef ? '👑 Admin' : player.connected ? 'جاهز للكشف' : 'منقطع مؤقتًا'}</small></div>
                {self.isChef && player.id !== self.id ? <button type="button" className="kick-btn" title="إخراج اللاعب" onClick={() => kick(player.id)}>×</button> : <span className={`presence ${player.connected ? 'on' : ''}`} />}
              </div>
            ))}
          </div>
        </div>

        {self.isChef ? (
          <aside className="glass-card chef-panel who-chef-panel">
            <span className="chef-label">👑 لوحة الأدمن</span>
            <h2>السؤال السري</h2>
            <p className="muted">اكتب سؤالًا يفتح المجال لإجابات مختلفة ومضحكة.</p>
            <textarea className="input textarea who-question-input" maxLength={180} value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="مثال: ما أسوأ شيء يمكن أن تفعله في أول موعد؟" />
            <div className="who-question-suggestions">
              {WHO_QUESTION_SUGGESTIONS.map((item, index) => <button type="button" key={index} onClick={() => setQuestion(item)}>{index + 1}</button>)}
            </div>

            <div className="setting-divider">الوقت</div>
            <TimeSetting label="وقت الإجابة" value={settings.answerSeconds} min={20} max={300} onChange={(value) => patch('answerSeconds', value)} />
            <TimeSetting label="وقت كل تخمين" value={settings.guessSeconds} min={10} max={180} onChange={(value) => patch('guessSeconds', value)} />
            <div className="who-points-note">🏆 كل تخمين صحيح = <strong>{settings.pointsPerCorrect} نقطة</strong></div>

            <button type="button" className="primary-btn xl full" onClick={start} disabled={busy || !canStart}>
              {busy ? 'جاري البدء...' : canStart ? 'إرسال السؤال وبدء اللعبة ←' : question.trim().length < 4 ? 'اكتب السؤال أولًا' : `نحتاج ${room.minPlayers} لاعبين`}
            </button>
          </aside>
        ) : (
          <aside className="glass-card waiting-panel"><div className="waiting-orbit"><span>🤫</span></div><h2>بانتظار الأدمن</h2><p>سيظهر السؤال على شاشتك بمجرد أن يبدأ الأدمن الجولة.</p></aside>
        )}
      </div>
    </section>
  );
}

function WhoAnswerScreen({ self, room, task, notify }: { self: Player; room: WhoSaidState; task: Extract<WhoSaidTask, { kind: 'who-answer' }> | null; notify: (message: string) => void }) {
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);

  if (self.isSpectator) {
    return <WhoSpectatorCard icon="🤫" title="الإجابات تُكتب الآن" text="دخلت كمشاهد. لن ترى الإجابات قبل مرحلة الكشف النهائية." room={room} />;
  }

  if (!task) return <section className="center-state glass-card"><div className="spinner" /><h2>تجهيز السؤال...</h2></section>;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!answer.trim() || task.submitted) return;
    setBusy(true);
    try {
      const result = await emitAck('who:submitAnswer', { value: answer.trim() });
      if (!result.ok) throw new Error(result.error);
      setAnswer('');
    } catch (error) {
      notify(messageOf(error));
    } finally {
      setBusy(false);
    }
  };

  const progress = Math.round((room.answerCount / Math.max(1, room.expectedAnswerCount)) * 100);

  if (task.submitted) {
    return (
      <section className="submitted-card glass-card who-submitted-card">
        <div className="success-ring">✓</div><span className="eyebrow">إجابتك محفوظة بسرية</span><h1>ولا كلمة لأحد! 🤫</h1>
        <p>بانتظار بقية اللاعبين. هويتك لن تظهر أثناء التخمين.</p>
        <div className="submission-progress"><span style={{ width: `${progress}%` }} /></div><strong>{room.answerCount} من {room.expectedAnswerCount} أجابوا</strong>
        {room.answerEndsAt && <Countdown endsAt={room.answerEndsAt} />}
      </section>
    );
  }

  return (
    <section className="who-answer-stage game-layout">
      <div className="game-header">
        <div className="round-info"><span>مرحلة الإجابات السرية · {room.answerCount}/{room.expectedAnswerCount}</span><div className="round-track"><i style={{ width: `${progress}%` }} /></div></div>
        {room.answerEndsAt && <Countdown endsAt={room.answerEndsAt} />}
      </div>
      <div className="glass-card who-question-hero"><span>السؤال</span><h1>{task.question}</h1><p>اكتب إجابة قصيرة ومميزة. لن يعرف الآخرون أنها منك حتى نهاية اللعبة.</p></div>
      <form className="glass-card who-answer-form" onSubmit={submit}>
        <label className="field-label">إجابتك السرية</label>
        <textarea className="input textarea" value={answer} maxLength={180} autoFocus placeholder="اكتب شيئًا يجعلهم يحتارون..." onChange={(e) => setAnswer(e.target.value)} />
        <div className="input-footer"><span>{answer.length}/180</span><small>🔒 لا يتم عرض اسمك مع الإجابة</small></div>
        <button className="primary-btn xl full" disabled={busy || !answer.trim()}>{busy ? 'جاري الإرسال...' : 'إرسال الإجابة سرًا 🤫'}</button>
      </form>
    </section>
  );
}

function WhoReadyScreen({ self, room, notify }: { self: Player; room: WhoSaidState; notify: (message: string) => void }) {
  const [busy, setBusy] = useState(false);
  const begin = async () => {
    setBusy(true);
    try {
      const result = await emitAck('chef:whoBeginGuessing', {});
      if (!result.ok) throw new Error(result.error);
    } catch (error) {
      notify(messageOf(error));
      setBusy(false);
    }
  };
  return (
    <section className="glass-card who-ready-card">
      <div className="who-ready-icon">🗳️</div><span className="eyebrow">انتهت مرحلة الإجابات</span><h1>تم جمع {room.answerCount} إجابة سرية</h1>
      <p>لن نكشف أي صاحب إجابة الآن. ستظهر الإجابات واحدة تلو الأخرى ليخمن الجميع، ثم تُكشف الحقيقة كاملة في النهاية.</p>
      {self.isChef ? <button className="primary-btn xl" onClick={begin} disabled={busy || room.answerCount < 2}>{busy ? 'جاري البدء...' : 'ابدأ مرحلة التخمين ←'}</button> : <div className="waiting-pulse"><i /><span>بانتظار الأدمن لبدء التخمين...</span></div>}
    </section>
  );
}

function WhoGuessScreen({ self, room, task, notify }: { self: Player; room: WhoSaidState; task: Extract<WhoSaidTask, { kind: 'who-guess' }> | null; notify: (message: string) => void }) {
  const [selected, setSelected] = useState<string>('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setSelected(task?.selectedPlayerId ?? ''); setBusy(false); }, [task?.answerId, task?.selectedPlayerId]);

  if (self.isSpectator) {
    return <WhoSpectatorCard icon="🕵️" title="التخمين جارٍ الآن" text={`الإجابة ${Math.min((room.guessIndex ?? 0) + 1, room.guessTotal)} من ${room.guessTotal}. النتائج ستظهر فقط في النهاية.`} room={room} />;
  }
  if (!task) return <section className="center-state glass-card"><div className="spinner" /><h2>تجهيز الإجابة التالية...</h2></section>;

  const submit = async () => {
    if (!selected || task.submitted || task.isOwnAnswer) return;
    setBusy(true);
    try {
      const result = await emitAck('who:submitGuess', { answerId: task.answerId, guessedPlayerId: selected });
      if (!result.ok) throw new Error(result.error);
    } catch (error) {
      notify(messageOf(error));
      setBusy(false);
    }
  };

  const step = task.index + 1;
  const progress = Math.round((step / Math.max(1, task.total)) * 100);
  const submittedCount = room.guessSubmittedCount ?? room.submittedPlayerIds?.length ?? 0;
  const expectedGuessCount = room.guessExpectedCount ?? Math.max(0, room.expectedAnswerCount - 1);

  return (
    <section className="who-guess-stage game-layout">
      <div className="game-header">
        <div className="round-info"><span>الإجابة {step} من {task.total} · صوّت بسرية</span><div className="round-track"><i style={{ width: `${progress}%` }} /></div></div>
        <div className="game-header-right"><span className="submitted-mini">✓ {submittedCount} من {expectedGuessCount} خمنوا</span>{room.guessEndsAt && <Countdown endsAt={room.guessEndsAt} />}</div>
      </div>

      <div className="glass-card anonymous-answer-card"><span className="anonymous-badge">إجابة مجهولة الهوية</span><blockquote>“{task.answer}”</blockquote><p>من تعتقد أنه قال هذا؟</p></div>

      {task.isOwnAnswer ? (
        <div className="glass-card own-answer-wait"><span>😏</span><h2>أنت تعرف هذه جيدًا...</h2><p>هذه إجابتك، لذلك لن تصوّت عليها ولن تحصل على نقطة مجانية. انتظر تخمينات الآخرين.</p><div className="waiting-pulse"><i /><span>بانتظار بقية اللاعبين...</span></div></div>
      ) : task.submitted ? (
        <div className="glass-card own-answer-wait"><span>🔒</span><h2>تم تسجيل اختيارك</h2><p>لن نكشف إن كنت محقًا الآن. الحقيقة ستظهر بعد انتهاء جميع الإجابات.</p><div className="waiting-pulse"><i /><span>بانتظار بقية اللاعبين...</span></div></div>
      ) : (
        <div className="who-candidates-wrap">
          <div className="who-candidates-grid">
            {task.candidates.map((player, index) => (
              <button type="button" key={player.id} className={`who-candidate ${selected === player.id ? 'selected' : ''}`} onClick={() => setSelected(player.id)}>
                <FunnyAvatar name={player.nickname} className="who-candidate-avatar" style={{ '--avatar-index': index } as CSSProperties} />
                <strong>{player.nickname}</strong><small>{selected === player.id ? 'اختيارك' : 'هل هو؟'}</small><i>{selected === player.id ? '✓' : '?'}</i>
              </button>
            ))}
          </div>
          <button type="button" className="primary-btn xl who-vote-submit" onClick={submit} disabled={!selected || busy}>{busy ? 'جاري تثبيت الاختيار...' : 'تثبيت اختياري 🔒'}</button>
        </div>
      )}
    </section>
  );
}

function WhoResultsScreen({ self, room, payload, notify }: { self: Player; room: WhoSaidState; payload: WhoSaidResultsPayload | null; notify: (message: string) => void }) {
  const [busy, setBusy] = useState(false);
  const data = payload ?? { results: [], scores: room.scores };
  const scores = [...data.scores].sort((a, b) => b.score - a.score || b.correct - a.correct);

  const reset = async () => {
    setBusy(true);
    try {
      const result = await emitAck('chef:whoReset', {});
      if (!result.ok) throw new Error(result.error);
    } catch (error) {
      notify(messageOf(error));
      setBusy(false);
    }
  };

  if (!data.results.length) return <section className="center-state glass-card"><div className="spinner" /><h2>حساب النتائج النهائية...</h2></section>;

  return (
    <section className="who-results-stage">
      <div className="who-results-heading"><span className="eyebrow">الحقيقة انكشفت</span><h1>من قال هذا؟ — النتائج النهائية</h1><p>الآن فقط تظهر هوية أصحاب الإجابات، نسب التصويت، ومن عرف الحقيقة.</p></div>

      <div className="glass-card who-scoreboard">
        <div className="card-header-row"><div><h2>🏆 ترتيب اللاعبين</h2><p className="muted">{room.whoSettings.pointsPerCorrect} نقطة لكل تخمين صحيح</p></div></div>
        <div className="who-score-grid">
          {scores.map((score, index) => (
            <div className={`who-score-card rank-${index + 1}`} key={score.playerId}>
              <span className="rank-medal">{index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}</span>
              <FunnyAvatar name={score.nickname} className="who-score-avatar" style={{ '--avatar-index': index } as CSSProperties} />
              <div><strong>{score.nickname}</strong><small>{score.correct} تخمين صحيح</small></div><b>{score.score}</b><em>نقطة</em>
            </div>
          ))}
        </div>
      </div>

      <div className="who-result-list">
        {data.results.map((result, answerIndex) => (
          <article className="glass-card who-result-card" key={result.answerId}>
            <div className="who-result-number">{answerIndex + 1}</div>
            <div className="who-result-answer"><span>الإجابة</span><blockquote>“{result.answer}”</blockquote></div>
            <div className="who-true-author">
              <span>القائل الحقيقي</span><div><FunnyAvatar name={result.authorNickname} className="who-result-author-avatar" /><strong>{result.authorNickname}</strong><b>✓ الحقيقة</b></div>
            </div>
            <div className="who-distribution">
              <h3>من اختاره اللاعبون؟ <small>{result.totalVotes} تصويت</small></h3>
              {result.distribution.map((item) => (
                <div className={`who-distribution-row ${item.playerId === result.authorId ? 'correct-author' : ''}`} key={item.playerId}>
                  <div className="who-distribution-name"><FunnyAvatar name={item.nickname} className="who-dist-avatar" /><strong>{item.nickname}</strong>{item.playerId === result.authorId && <span>الحقيقي</span>}</div>
                  <div className="who-distribution-bar"><i style={{ width: `${item.percentage}%` }} /></div>
                  <b>{item.percentage}%</b><small>{item.votes}</small>
                </div>
              ))}
            </div>
            <div className="who-correct-guessers">
              <span>🎯 الذين عرفوا القائل</span>
              {result.correctGuessers.length ? <div>{result.correctGuessers.map((winner) => <span key={winner.id}><FunnyAvatar name={winner.nickname} className="who-winner-avatar" />{winner.nickname}<b>+{room.whoSettings.pointsPerCorrect}</b></span>)}</div> : <p>لم يعرفه أحد 😈</p>}
            </div>
          </article>
        ))}
      </div>

      {self.isChef && <div className="who-results-actions"><button className="primary-btn xl" onClick={reset} disabled={busy}>{busy ? 'جاري التجهيز...' : 'لعبة جديدة بسؤال جديد ↻'}</button></div>}
    </section>
  );
}

function WhoSpectatorCard({ icon, title, text, room }: { icon: string; title: string; text: string; room: WhoSaidState }) {
  return (
    <section className="glass-card who-ready-card"><div className="who-ready-icon">{icon}</div><span className="eyebrow">وضع المشاهدة</span><h1>{title}</h1><p>{text}</p><div className="waiting-pulse"><i /><span>{room.players.filter((p) => p.connected && !p.isSpectator).length} لاعبًا في الجولة</span></div></section>
  );
}

function Lobby({ self, room, notify, onOpenArchive }: { self: Player; room: RoomState; notify: (message: string) => void; onOpenArchive: () => void }) {
  const [settings, setSettings] = useState(room.settings);
  const [busy, setBusy] = useState(false);

  useEffect(() => setSettings(room.settings), [room.settings]);

  const patch = <K extends keyof RoomState['settings']>(key: K, value: RoomState['settings'][K]) => {
    setSettings((current) => ({ ...current, [key]: value }));
  };

  const saveSettings = async () => {
    try {
      const result = await emitAck('chef:settings', settings);
      if (!result.ok) throw new Error(result.error);
      notify('تم حفظ إعدادات اللعبة والألبوم.');
    } catch (error) {
      notify(messageOf(error));
    }
  };

  const start = async () => {
    setBusy(true);
    try {
      const save = await emitAck('chef:settings', settings);
      if (!save.ok) throw new Error(save.error);
      const result = await emitAck('chef:start', {});
      if (!result.ok) throw new Error(result.error);
    } catch (error) {
      notify(messageOf(error));
      setBusy(false);
    }
  };

  const canStart = room.players.filter((p) => p.connected).length >= room.minPlayers;

  const kick = async (targetId: string) => {
    try {
      const result = await emitAck('chef:kick', { targetId });
      if (!result.ok) throw new Error(result.error);
      notify('تم إخراج اللاعب من الغرفة.');
    } catch (error) {
      notify(messageOf(error));
    }
  };

  return (
    <section className="lobby-layout">
      <div className="section-heading">
        <div><span className="eyebrow">غرفة الانتظار</span><h1>الجميع هنا؟</h1><p>عندما يبدأ الأدمن، سيكتب كل لاعب جملة ثم تبدأ رحلة الرسم والتخمين.</p></div>
        <div className="room-stat"><strong>{room.players.filter((p) => p.connected).length}</strong><span>لاعب متصل</span></div>
      </div>

      <div className={`lobby-grid ${self.isChef ? 'with-controls' : ''}`}>
        <div className="glass-card players-card">
          <div className="card-header-row"><div><h2>اللاعبون</h2><p className="muted">بحد أقصى {room.maxPlayers} لاعبًا</p></div><span className="live-badge"><i /> مباشر</span></div>
          <div className="players-grid">
            {room.players.map((player, index) => (
              <div className={`player-card ${!player.connected ? 'disconnected' : ''}`} key={player.id}>
                <FunnyAvatar as="div" name={player.nickname} className="avatar" style={{ '--avatar-index': index } as CSSProperties} />
                <div className="player-meta"><strong>{player.nickname}</strong><small>{player.isChef ? '👑 Admin' : player.connected ? 'جاهز' : 'منقطع مؤقتًا'}</small></div>
                {self.isChef && player.id !== self.id ? <button type="button" className="kick-btn" title="إخراج اللاعب" onClick={() => kick(player.id)}>×</button> : <span className={`presence ${player.connected ? 'on' : ''}`} />}
              </div>
            ))}
          </div>
        </div>

        {self.isChef ? (
          <aside className="glass-card chef-panel">
            <span className="chef-label">👑 لوحة الأدمن</span>
            <h2>إعداد اللعبة</h2>
            <p className="muted">إعدادات اللعب ثم طريقة عرض الألبوم بعد انتهاء الجولات.</p>

            <TimeSetting label="وقت الكتابة" value={settings.textSeconds} min={20} max={300} onChange={(value) => patch('textSeconds', value)} />
            <TimeSetting label="وقت الرسم" value={settings.drawSeconds} min={30} max={600} onChange={(value) => patch('drawSeconds', value)} />

            <div className="setting-divider">لوحة الرسم</div>
            <SegmentedSetting
              label="شكل اللوحة"
              value={settings.canvasFormat}
              options={[['standard', 'عريضة'], ['square', 'مربعة']]}
              onChange={(value) => patch('canvasFormat', value as 'standard' | 'square')}
            />

            <div className="setting-divider">عرض الألبوم</div>
            <SegmentedSetting
              label="طريقة العرض"
              value={settings.albumDisplay}
              options={[['auto', 'تلقائي'], ['manual', 'يدوي']]}
              onChange={(value) => patch('albumDisplay', value as 'auto' | 'manual')}
            />
            <SegmentedSetting
              label="ترتيب الألبومات"
              value={settings.albumDirection}
              options={[['forward', 'من البداية'], ['reverse', 'من النهاية']]}
              onChange={(value) => patch('albumDirection', value as 'forward' | 'reverse')}
            />
            <ToggleSetting label="قراءة الجمل صوتيًا أثناء الألبوم" checked={settings.albumVoiceover} onChange={(value) => patch('albumVoiceover', value)} />

            <button type="button" className="secondary-btn full archive-panel-btn" onClick={onOpenArchive}>🗂️ فتح أرشيف الألعاب</button>
            <button type="button" className="secondary-btn full" onClick={saveSettings}>حفظ الإعدادات</button>
            <button type="button" className="primary-btn xl full" onClick={start} disabled={!canStart || busy}>{busy ? 'جاري البدء...' : 'ابدأ اللعبة'} <span>▶</span></button>
            {!canStart && <p className="tiny-note">يلزم {room.minPlayers} لاعبين متصلين على الأقل.</p>}
          </aside>
        ) : (
          <aside className="glass-card waiting-panel"><div className="waiting-orbit"><span>✦</span></div><h2>بانتظار الأدمن</h2><p>استرخِ قليلًا. ستبدأ اللعبة عندما يكون الجميع جاهزًا ويقرر الأدمن البدء.</p></aside>
        )}
      </div>
    </section>
  );
}

function GameScreen({ self, room, task, notify }: { self: Player; room: RoomState; task: SketchGameTask | null; notify: (message: string) => void }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef<DrawingCanvasHandle | null>(null);

  useEffect(() => {
    setText(task?.kind === 'text' ? (task.draftText ?? '') : '');
    setBusy(false);
  }, [task?.gameId, task?.turn, task?.kind, task?.assignmentId]);

  useEffect(() => {
    if (!task || task.kind !== 'text' || task.submitted || !text.trim()) return;
    const timer = window.setTimeout(() => {
      if (!socket.connected) return;
      socket.volatile.emit('game:textDraft', {
        value: text.trim(),
        gameId: task.gameId,
        turn: task.turn,
        assignmentId: task.assignmentId
      });
    }, 700);
    return () => window.clearTimeout(timer);
  }, [task?.gameId, task?.turn, task?.assignmentId, task?.kind, task?.submitted, text]);

  const drawingDirtyRef = useRef(false);
  const draftChangeTimerRef = useRef<number | null>(null);

  const saveDrawingDraft = useCallback((options: { force?: boolean; reliable?: boolean } = {}) => {
    if (!task || task.kind !== 'drawing' || task.submitted) return;
    if (!options.force && !drawingDirtyRef.current) return;
    // Routine autosaves are intentionally volatile: if the connection is down,
    // dropping one snapshot is safer than queueing several multi-hundred-KB
    // images and flooding a large 16-player room when the phone reconnects.
    if (!options.reliable && !socket.connected) return;
    try {
      const submission = canvasRef.current?.exportSubmission();
      if (!submission) return;
      const payload = {
        value: submission.image,
        replay: submission.replay,
        gameId: task.gameId,
        turn: task.turn,
        assignmentId: task.assignmentId
      };
      if (options.reliable) socket.emit('game:drawingDraft', payload);
      else socket.volatile.emit('game:drawingDraft', payload);
      drawingDirtyRef.current = false;
    } catch {
      // Canvas can briefly be unavailable while switching relay turns.
    }
  }, [task?.gameId, task?.turn, task?.assignmentId, task?.kind, task?.submitted]);

  const queueDrawingDraft = useCallback(() => {
    drawingDirtyRef.current = true;
    // Throttle instead of debouncing every stroke. At most one routine snapshot
    // is sent per ~2.2s, even if the player draws many short strokes quickly.
    if (draftChangeTimerRef.current !== null) return;
    draftChangeTimerRef.current = window.setTimeout(() => {
      draftChangeTimerRef.current = null;
      saveDrawingDraft();
    }, 2200);
  }, [saveDrawingDraft]);

  useEffect(() => {
    drawingDirtyRef.current = false;
    if (!task || task.kind !== 'drawing' || task.submitted) return;

    // Only changed drawings are periodically snapshotted. Near the shared
    // deadline we force two reliable snapshots so a partially finished drawing
    // can be accepted even when the player never taps "إرسال الرسم".
    const interval = window.setInterval(() => saveDrawingDraft(), 6000);
    const finalOffsets = [2200, 650];
    const finalTimers = finalOffsets.map((offset) =>
      window.setTimeout(
        () => saveDrawingDraft({ force: true, reliable: true }),
        Math.max(0, task.endsAt - Date.now() - offset)
      )
    );

    return () => {
      window.clearInterval(interval);
      finalTimers.forEach((timer) => window.clearTimeout(timer));
      if (draftChangeTimerRef.current !== null) {
        window.clearTimeout(draftChangeTimerRef.current);
        draftChangeTimerRef.current = null;
      }
    };
  }, [task?.gameId, task?.turn, task?.assignmentId, task?.kind, task?.submitted, task?.endsAt, saveDrawingDraft]);

  const submitted = Boolean(task?.submitted || room.submittedPlayerIds?.includes(self.id));
  const submittedCount = room.submittedPlayerIds?.length ?? 0;
  const total = room.participantPlayerIds?.length ?? room.totalTurns ?? room.players.filter((player) => !player.isSpectator).length;
  const isParticipant = Boolean(room.participantPlayerIds?.includes(self.id));

  const submitText = async () => {
    if (!task || task.kind !== 'text') return;
    if (!text.trim()) return notify('اكتب وصفًا أولًا.');
    setBusy(true);
    try {
      const result = await emitAck('game:submitText', {
        value: text.trim(),
        gameId: task.gameId,
        turn: task.turn,
        assignmentId: task.assignmentId
      });
      if (!result.ok) throw new Error(result.error);
    } catch (error) {
      notify(messageOf(error));
      setBusy(false);
    }
  };

  const submitDrawing = async () => {
    if (!task || task.kind !== 'drawing') return;
    setBusy(true);
    try {
      const submission = canvasRef.current?.exportSubmission();
      if (!submission) throw new Error('تعذر قراءة الرسم.');
      const result = await emitAck('game:submitDrawing', {
        value: submission.image,
        gameId: task.gameId,
        turn: task.turn,
        assignmentId: task.assignmentId
      });
      if (!result.ok) throw new Error(result.error);
    } catch (error) {
      notify(messageOf(error));
      setBusy(false);
    }
  };

  const forceNext = async () => {
    try {
      const result = await emitAck('chef:next', { gameId: room.sessionId, turn: room.turn });
      if (!result.ok) throw new Error(result.error);
    } catch (error) {
      notify(messageOf(error));
    }
  };

  if (!task) {
    // A submitted player has no active assignment on the server. This is a
    // normal waiting state (especially after reconnect), never a spectator or
    // "preparing chains" state.
    if (isParticipant && submitted) {
      return (
        <section className="waiting-game">
          <SketchRoomHeader room={room} submittedCount={submittedCount} total={total} timerAlarm />
          <div className="glass-card submitted-card">
            <div className="success-ring">✓</div><h1>وصلت!</h1><p>تم حفظ إجابتك. ننتظر بقية اللاعبين للانتقال إلى الجولة التالية.</p>
            <div className="submission-progress"><span style={{ width: `${Math.min(100, (submittedCount / Math.max(1, total)) * 100)}%` }} /></div>
            <strong>{submittedCount} / {total} أرسلوا</strong>
            {self.isChef && <button className="secondary-btn" onClick={forceNext}>تخطي الانتظار والانتقال الآن</button>}
          </div>
        </section>
      );
    }

    if (self.isChef && !isParticipant) {
      return (
        <section className="center-state glass-card admin-observer">
          <div className="observer-icon">👑</div><h2>وضع المراقبة</h2>
          <p>دخلت كأدمن بعد بدء اللعبة، لذلك تراقب الجولة دون المشاركة فيها. يمكنك الانتقال للجولة التالية أو بدء لعبة جديدة من لوحة التحكم.</p>
          <strong>{submittedCount} / {total} أرسلوا</strong>
          <button className="secondary-btn" onClick={forceNext}>الانتقال للجولة التالية</button>
        </section>
      );
    }

    return (
      <section className="waiting-game">
        <SketchRoomHeader room={room} submittedCount={submittedCount} total={total} timerAlarm />
        <div className="glass-card submitted-card">
          <div className="spinner" />
          <h2>مزامنة مهمتك...</h2>
          <p>الاتصال موجود، ويتم الآن استعادة نفس المهمة من الخادم. لن تُنقل مهمتك إلى لاعب آخر.</p>
        </div>
      </section>
    );
  }

  if (submitted) {
    return (
      <section className="waiting-game">
        <GameHeader task={task} submittedCount={submittedCount} total={total} settings={room.settings} timerAlarm />
        <div className="glass-card submitted-card">
          <div className="success-ring">✓</div><h1>وصلت!</h1><p>تم حفظ إجابتك. ننتظر بقية اللاعبين للانتقال إلى الجولة التالية.</p>
          <div className="submission-progress"><span style={{ width: `${Math.min(100, (submittedCount / Math.max(1, total)) * 100)}%` }} /></div>
          <strong>{submittedCount} / {total} أرسلوا</strong>
          {self.isChef && <button className="secondary-btn" onClick={forceNext}>تخطي الانتظار والانتقال الآن</button>}
        </div>
      </section>
    );
  }

  return (
    <section className="game-layout">
      <GameHeader task={task} submittedCount={submittedCount} total={total} settings={room.settings} timerAlarm />
      {task.kind === 'drawing' ? (
        <div className="game-workspace drawing-workspace">
          <div className="prompt-banner"><span>مهمتك</span><h2>« {task.promptText} »</h2><p>ارسم الجملة كما تتخيلها، بدون كتابة كلمات داخل الرسم 😉</p></div>
          <DrawingCanvas
            key={`${task.gameId}:${task.turn}:${task.assignmentId}`}
            ref={canvasRef}
            canvasFormat={task.canvasFormat}
            initialImage={task.draftImage}
            initialReplay={task.draftReplay}
            onDrawingChange={queueDrawingDraft}
          />
          <div className="game-actions">
            {self.isChef && <button className="ghost-btn" onClick={forceNext}>تخطي الجولة</button>}
            <button className="primary-btn xl" onClick={submitDrawing} disabled={busy}>{busy ? 'جاري الإرسال...' : 'إرسال الرسم'} <span>✓</span></button>
          </div>
        </div>
      ) : (
        <div className="game-workspace text-workspace">
          {task.promptDrawing ? (
            <><div className="prompt-banner compact"><span>شاهد الرسم</span><h2>ماذا تعتقد أنه يعني؟</h2></div><div className={`guess-image-frame canvas-${task.canvasFormat}`}><img src={task.promptDrawing} alt="رسم اللاعب السابق" /></div></>
          ) : (
            <div className="prompt-banner first-prompt"><span>الجولة الأولى</span><h2>اكتب جملة ممتعة ليقوم شخص آخر برسمها</h2><p>كلما كانت الفكرة واضحة وغريبة قليلًا، كانت النتيجة أمتع.</p></div>
          )}
          <div className="glass-card text-submit-card">
            <label className="field-label">{task.promptDrawing ? 'اكتب وصفك للرسم' : 'اكتب الجملة'}</label>
            <textarea className="input textarea" value={text} maxLength={180} autoFocus placeholder={task.promptDrawing ? 'مثال: قطة تطير فوق مدينة...' : 'مثال: بطريق يشرب القهوة على القمر...'} onChange={(e) => setText(e.target.value)} />
            <div className="input-footer"><span>{text.length}/180</span><small>لا تذكر للاعب التالي ما شاهدته قبل ذلك.</small></div>
            <button className="primary-btn xl full" onClick={submitText} disabled={busy || !text.trim()}>{busy ? 'جاري الإرسال...' : 'إرسال'} <span>←</span></button>
          </div>
          {self.isChef && <button className="ghost-btn centered" onClick={forceNext}>تخطي الجولة</button>}
        </div>
      )}
    </section>
  );
}

function SketchRoomHeader({ room, submittedCount, total, timerAlarm = false }: { room: RoomState; submittedCount: number; total: number; timerAlarm?: boolean }) {
  const isDrawing = room.turnKind === 'drawing';
  const totalSeconds = isDrawing ? room.settings.drawSeconds : room.settings.textSeconds;
  const turn = room.turn ?? 0;
  const totalTurns = room.totalTurns ?? Math.max(1, total);
  return (
    <div className="game-header">
      <div className="round-info"><span>الجولة {turn + 1} من {totalTurns}</span><div className="round-track"><i style={{ width: `${((turn + 1) / Math.max(1, totalTurns)) * 100}%` }} /></div></div>
      <div className="game-header-right">
        <span className="submitted-mini">✓ {submittedCount}/{total}</span>
        {room.endsAt ? <Countdown endsAt={room.endsAt} totalSeconds={totalSeconds} hourglass warningSound={timerAlarm} bellAtSeconds={15} activityLabel={isDrawing ? 'وقت الرسم' : 'وقت الكتابة'} /> : null}
      </div>
    </div>
  );
}

function SpectatorPlayingScreen({ room }: { room: RoomState }) {
  const activePlayers = room.players.filter((player) => !player.isSpectator);
  return (
    <section className="center-state glass-card spectator-playing-card">
      <div className="album-stack-visual"><span /><span /><span>👁️</span></div>
      <span className="eyebrow">وضع المشاهدة</span>
      <h2>هناك لعبة جديدة جارية الآن.</h2>
      <p>أنت متصل كمشاهد. عند انتهاء الجولات ستنتقل تلقائيًا إلى شاشة انتظار الألبوم ثم تشاهد العرض مع الجميع.</p>
      <div className="waiting-pulse"><i /><span>{activePlayers.length} لاعب في الجولة الحالية</span></div>
    </section>
  );
}

function GameHeader({
  task,
  submittedCount,
  total,
  settings,
  timerAlarm = false,
}: {
  task: SketchGameTask;
  submittedCount: number;
  total: number;
  settings: GameSettings;
  timerAlarm?: boolean;
}) {
  const isDrawing = task.kind === 'drawing';
  const totalSeconds = isDrawing ? settings.drawSeconds : settings.textSeconds;

  return (
    <div className="game-header">
      <div className="round-info"><span>الجولة {task.turn + 1} من {task.totalTurns}</span><div className="round-track"><i style={{ width: `${((task.turn + 1) / task.totalTurns) * 100}%` }} /></div></div>
      <div className="game-header-right">
        <span className="submitted-mini">✓ {submittedCount}/{total}</span>
        <Countdown
          endsAt={task.endsAt}
          totalSeconds={totalSeconds}
          hourglass
          warningSound={timerAlarm}
          bellAtSeconds={15}
          activityLabel={isDrawing ? 'وقت الرسم' : 'وقت الكتابة'}
        />
      </div>
    </div>
  );
}

function AlbumWaitingScreen({ self, room, notify }: { self: Player; room: RoomState; notify: (message: string) => void }) {
  const [busy, setBusy] = useState(false);
  const startAlbum = async () => {
    setBusy(true);
    try {
      const result = await emitAck('chef:albumStart', {});
      if (!result.ok) throw new Error(result.error);
      notify('تم إرسال الجولة للجميع. يبدأ العرض بعد اكتمال مزامنة الأجهزة.');
    } catch (error) {
      notify(messageOf(error));
      setBusy(false);
    }
  };

  return (
    <section className="album-wait-screen">
      <div className="glass-card album-wait-card">
        <div className="album-stack-visual"><span /><span /><span>🎬</span></div>
        <span className="eyebrow">انتهت الجولات</span>
        <h1>الألبوم جاهز.</h1>
        {self.isChef ? (
          <>
            <p>الجميع الآن ينتظر. عندما تضغط الزر سيبدأ عرض الألبوم عند جميع اللاعبين في نفس اللحظة.</p>
            <div className="album-summary">
              <span>العرض: <strong>{room.settings.albumDisplay === 'auto' ? 'تلقائي' : 'يدوي'}</strong></span>
              <span>الترتيب: <strong>{room.settings.albumDirection === 'forward' ? 'من البداية' : 'من النهاية'}</strong></span>
              <span>الرسومات: <strong>الصورة النهائية مباشرة</strong></span>
            </div>
            <button className="primary-btn xl album-send-btn" onClick={startAlbum} disabled={busy}>{busy ? 'جاري الإرسال...' : 'إرسال الألبوم وبدء المشاهدة'} <span>▶</span></button>
          </>
        ) : (
          <><p>{self.isSpectator ? 'دخلت كمشاهد بنجاح. الألبوم جاهز، وسيظهر لك تلقائيًا عندما يرسله الأدمن.' : 'لا تظهر النتائج بعد. انتظر حتى يقوم الأدمن بإرسال الألبوم للمشاهدة.'}</p><div className="waiting-pulse"><i /><span>{self.isSpectator ? 'مشاهد · بانتظار بدء العرض...' : 'بانتظار الأدمن...'}</span></div></>
        )}
      </div>
    </section>
  );
}

function RevealScreen({ self, chains, room, serverClockOffsetMs, notify }: { self: Player; chains: GameChain[]; room: RoomState; serverClockOffsetMs: number; notify: (message: string) => void }) {
  const authoritativeAlbum = room.album;
  const album = useSynchronizedAlbum(authoritativeAlbum, room.sessionId, serverClockOffsetMs);
  const chain = album ? chains[album.chainIndex] : undefined;
  const currentEntry = chain && album ? chain.entries[album.entryIndex] : undefined;
  const transitionPending = Boolean(authoritativeAlbum && album && authoritativeAlbum.revision !== album.revision);
  const latestRef = useRef<HTMLDivElement | null>(null);
  const [collapsedPlayers, setCollapsedPlayers] = useState(false);
  const [exportingGif, setExportingGif] = useState(false);
  const [gifProgress, setGifProgress] = useState(0);
  const [gifStatus, setGifStatus] = useState('');
  const [archiveStatus, setArchiveStatus] = useState<'idle' | 'building' | 'uploading' | 'saved' | 'skipped' | 'error'>('idle');
  const [archiveProgress, setArchiveProgress] = useState(0);
  const [archiveError, setArchiveError] = useState('');

  useEffect(() => {
    if (!album) return;
    const timer = window.setTimeout(() => {
      const element = latestRef.current;
      if (!element) return;

      const rect = element.getBoundingClientRect();

      // Keep the newly revealed album step comfortably below the top
      // instead of aligning it with the bottom edge of the screen.
      const topOffset = Math.min(
        150,
        Math.max(90, window.innerHeight * 0.14)
      );

      const targetTop =
        window.scrollY +
        rect.top -
        topOffset;

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth'
      });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [album?.chainIndex, album?.entryIndex]);

  useEffect(() => {
    if (!room.settings.albumVoiceover || currentEntry?.kind !== 'text' || !('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(currentEntry.value);
    utterance.lang = 'ar';
    utterance.rate = 1.08;
    window.speechSynthesis.speak(utterance);
    return () => window.speechSynthesis.cancel();
  }, [album?.chainIndex, album?.entryIndex, currentEntry?.value, currentEntry?.kind, room.settings.albumVoiceover]);

  useEffect(() => {
    // Every displayed chain gets a fresh explicit archive decision. Nothing is
    // saved automatically, including the last round.
    setArchiveStatus('idle');
    setArchiveProgress(0);
    setArchiveError('');
  }, [room.sessionId, authoritativeAlbum?.chainIndex]);

  const control = async (event: 'chef:albumNext' | 'chef:albumPrevious' | 'chef:albumToggle') => {
    try {
      const result = await emitAck(event, { gameId: room.sessionId, chainIndex: album?.chainIndex, entryIndex: album?.entryIndex, revision: album?.revision });
      if (!result.ok) throw new Error(result.error);
    } catch (error) {
      notify(messageOf(error));
    }
  };

  const saveRoundArchive = async () => {
    if (!self.isChef || !album?.chainFinished || !chain || !room.sessionId) return;
    if (archiveStatus === 'building' || archiveStatus === 'uploading' || archiveStatus === 'saved') return;

    setArchiveStatus('building');
    setArchiveProgress(0);
    setArchiveError('');
    try {
      const token = readPersistentValue(tokenKey('sketch')) || '';
      const result = await createAndUploadRoundArchive(
        room.sessionId,
        album.chainIndex,
        chain,
        room.settings.canvasFormat,
        token,
        (percent, label) => {
          setArchiveProgress(percent);
          setArchiveStatus(percent >= 98 || label.includes('رفع') ? 'uploading' : 'building');
        }
      );
      setArchiveProgress(100);
      setArchiveStatus('saved');
      notify(result.alreadySaved ? `الجولة ${album.chainIndex + 1} محفوظة مسبقًا في الأرشيف.` : `تم حفظ الجولة ${album.chainIndex + 1} فقط في الأرشيف.`);
    } catch (error) {
      setArchiveStatus('error');
      setArchiveError(messageOf(error));
    }
  };

  const skipRoundArchive = () => {
    if (!self.isChef || archiveStatus === 'building' || archiveStatus === 'uploading' || archiveStatus === 'saved') return;
    setArchiveError('');
    setArchiveProgress(0);
    setArchiveStatus('skipped');
  };

  const nextRound = async () => {
    if (!album?.chainFinished || album.finished) return;
    if (archiveStatus !== 'saved' && archiveStatus !== 'skipped') {
      notify('اختر أولًا: حفظ هذه الجولة في الأرشيف أو عدم حفظها.');
      return;
    }
    try {
      const result = await emitAck('chef:albumNextChain', { gameId: room.sessionId, chainIndex: album.chainIndex, entryIndex: album.entryIndex, revision: album.revision });
      if (!result.ok) throw new Error(result.error);
    } catch (error) {
      notify(messageOf(error));
    }
  };

  const downloadGif = async () => {
    if (!chain || exportingGif) return;
    setExportingGif(true);
    setGifProgress(0);
    setGifStatus(`تجهيز GIF للجولة ${album ? album.chainIndex + 1 : ''}...`);
    try {
      // Export only the currently completed original-sentence chain.
      await exportAlbumGif([chain], room.settings.canvasFormat, (percent, label) => {
        setGifProgress(percent);
        setGifStatus(label);
      });
      notify(`تم إنشاء GIF للجولة ${album ? album.chainIndex + 1 : ''} وبدأ التحميل.`);
    } catch (error) {
      notify(messageOf(error));
    } finally {
      setExportingGif(false);
      window.setTimeout(() => {
        setGifProgress(0);
        setGifStatus('');
      }, 1200);
    }
  };

  const reset = async () => {
    if (self.isChef && album?.chainFinished && archiveStatus !== 'saved' && archiveStatus !== 'skipped') {
      notify('اختر أولًا: حفظ الجولة الأخيرة في الأرشيف أو عدم حفظها.');
      return;
    }
    try {
      const result = await emitAck('chef:reset', {});
      if (!result.ok) throw new Error(result.error);
    } catch (error) {
      notify(messageOf(error));
    }
  };

  if (!authoritativeAlbum || authoritativeAlbum.preparing || !album || album.preparing || !chain) {
    return (
      <section className="center-state glass-card album-sync-state">
        <div className="spinner" />
        <h2>جاري مزامنة الجولة على جميع الأجهزة…</h2>
        <p className="muted">لن تُعرض صورة قديمة أثناء تحميل الجولة. سيبدأ العرض تلقائيًا عند اكتمال المزامنة.</p>
      </section>
    );
  }

  const visibleEntries = chain.entries.slice(0, album.entryIndex + 1);
  const playerIndex = new Map(room.players.map((player, index) => [player.id, index]));
  const currentRound = album.chainIndex + 1;
  const totalRounds = Math.max(1, room.totalTurns ?? chains.length);
  const roundProgress = Math.min(100, ((album.entryIndex + 1) / Math.max(1, chain.entries.length)) * 100);

  return (
    <section className={`album-theater chat-album-theater ${collapsedPlayers ? 'wide' : ''}`}>
      <div className="album-theater-head chat-album-head">
        <div>
          <span className="eyebrow">عرض الجولة {currentRound} من {totalRounds}</span>
          <h1>ألبوم {chain.originNickname}</h1>
          <p>هذه الجولة هي السلسلة الكاملة للنص الأصلي حتى آخر لاعب · الرسالة {Math.min(album.entryIndex + 1, chain.entries.length)} من {chain.entries.length}</p>
        </div>
        <button className="ghost-btn" onClick={() => setCollapsedPlayers((v) => !v)}>{collapsedPlayers ? 'إظهار اللاعبين' : 'إخفاء اللاعبين'}</button>
      </div>

      {!collapsedPlayers && (
        <div className="album-player-row chat-player-row">
          {room.players.map((player, index) => (
            <span key={player.id} className={!player.connected ? 'offline' : ''}>
              <FunnyAvatar as="i" name={player.nickname} className="mini-avatar chat-top-avatar" style={{ '--avatar-index': index } as CSSProperties} />
              <b>{player.nickname}</b>
              {player.isChef && <em>♛</em>}
            </span>
          ))}
        </div>
      )}

      <div className="album-progress"><span style={{ width: `${roundProgress}%` }} /></div>

      <div className="album-chat-stage">
        <div className="album-chat-title"><span>💬</span><strong>الجولة {currentRound}: القصة كاملة من أول نص إلى آخر لاعب</strong><small>بعد اكتمالها يمكن للجميع تنزيل GIF خاص بهذه الجولة، ثم يبدأ الأدمن الجولة التالية</small></div>

        <div className="album-chat-thread" role="log" aria-live="polite">
          {visibleEntries.map((entry, index) => {
            const isCurrent = index === album.entryIndex;
            const nextEntry = chain.entries[index + 1];
            const side = index % 2 === 0 ? 'chat-right' : 'chat-left';
            const avatarIndex = playerIndex.get(entry.authorId) ?? index;

            return (
              <div className="chat-sequence" key={`${entry.turn}-${entry.authorId}`} ref={isCurrent ? latestRef : undefined}>
                <div className={`album-chat-message ${side} ${entry.kind} ${isCurrent ? 'current' : ''}`}>
                  <div className="chat-message-meta">
                    <FunnyAvatar name={entry.authorNickname} className="chat-avatar mini-avatar" style={{ '--avatar-index': avatarIndex } as CSSProperties} />
                    <div>
                      <strong>{entry.authorNickname}</strong>
                      <small>{entry.kind === 'text' ? 'كتب هذه الجملة' : 'رسم ما فهمه'}</small>
                    </div>
                  </div>

                  <article className={`chat-bubble ${entry.kind}`}>
                    {entry.kind === 'text' ? (
                      <blockquote className={isCurrent ? 'text-reveal' : ''}>{entry.value}</blockquote>
                    ) : (
                      <div className="album-drawing-review">
                        <img src={entry.value} alt={`رسم ${entry.authorNickname}`} />
                      </div>
                    )}
                    {entry.skipped && <small className="skipped-tag">انتهى الوقت</small>}
                  </article>
                </div>

                {nextEntry && (
                  <div className={`chat-handoff ${isCurrent ? 'pending' : ''}`}>
                    <span className="handoff-line" />
                    <div className="handoff-pill">
                      <i>➜</i>
                      <span>
                        أُرسلت إلى <strong>{nextEntry.authorNickname}</strong>
                        {' '}{nextEntry.kind === 'drawing' ? 'ليرسمها' : 'ليصف الرسم'}
                      </span>
                    </div>
                    <span className="handoff-line" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {album.chainFinished && (
        <div className="glass-card album-finished-card">
          <span>{album.finished ? '🏁' : '✨'}</span>
          <h2>{album.finished ? `انتهت الجولة ${currentRound} وهي آخر جولة` : `اكتمل ألبوم الجولة ${currentRound}`}</h2>
          <p>
            {album.finished
              ? 'اكتملت جميع الجولات. يمكن لكل شخص تنزيل GIF خاص بهذه الجولة الأخيرة.'
              : `يمكن للجميع الآن تنزيل الجولة ${currentRound} وحدها كملف GIF. لن تُعرض الجولة ${currentRound + 1} حتى يضغط الأدمن زر عرض الألبوم التالي.`}
          </p>

          {self.isChef && (
            <div className={`archive-save-status ${archiveStatus}`} aria-live="polite">
              {(archiveStatus === 'idle' || archiveStatus === 'error') && (
                <>
                  <strong>🗂️ هل تريد حفظ الجولة {currentRound} في الأرشيف؟</strong>
                  <small>لن يتم حفظ أي شيء تلقائيًا. إذا اخترت الحفظ فسيُحفظ GIF وبيانات هذه الجولة فقط، وليس اللعبة كاملة.</small>
                  {archiveStatus === 'error' && <small>⚠️ تعذر الحفظ: {archiveError}</small>}
                  <div className="album-download-actions">
                    <button type="button" className="primary-btn" onClick={saveRoundArchive} disabled={exportingGif}>
                      حفظ الجولة {currentRound} في الأرشيف ✓
                    </button>
                    <button type="button" className="secondary-btn" onClick={skipRoundArchive} disabled={exportingGif}>
                      عدم حفظ هذه الجولة
                    </button>
                  </div>
                </>
              )}

              {(archiveStatus === 'building' || archiveStatus === 'uploading') && (
                <>
                  <strong>جاري حفظ الجولة {currentRound} فقط…</strong>
                  <div className="gif-export-progress-track"><span style={{ width: `${archiveProgress}%` }} /></div>
                  <small>{archiveStatus === 'building' ? `إنشاء GIF للجولة… ${archiveProgress}%` : 'رفع الجولة إلى الأرشيف…'}</small>
                </>
              )}

              {archiveStatus === 'saved' && <small>✅ تم حفظ الجولة {currentRound} فقط في الأرشيف. لم يتم حفظ بقية جولات اللعبة تلقائيًا.</small>}
              {archiveStatus === 'skipped' && <small>⏭️ لن تُحفظ الجولة {currentRound} في الأرشيف.</small>}
            </div>
          )}

          <div className="album-download-actions">
            <button
              type="button"
              className="primary-btn album-gif-download"
              onClick={downloadGif}
              disabled={exportingGif || archiveStatus === 'building' || archiveStatus === 'uploading'}
            >
              {exportingGif ? `جاري إنشاء GIF… ${gifProgress}%` : `تحميل GIF للجولة ${currentRound} ⬇`}
            </button>

            {self.isChef && !album.finished && (archiveStatus === 'saved' || archiveStatus === 'skipped') && (
              <button type="button" className="secondary-btn" onClick={nextRound} disabled={exportingGif}>
                عرض ألبوم الجولة التالية ({currentRound + 1}/{totalRounds}) ←
              </button>
            )}

            {self.isChef && album.finished && (archiveStatus === 'saved' || archiveStatus === 'skipped') && (
              <button className="secondary-btn" onClick={reset} disabled={exportingGif}>العودة لغرفة الانتظار ↻</button>
            )}
          </div>

          {exportingGif && (
            <div className="gif-export-progress" aria-live="polite">
              <div className="gif-export-progress-track"><span style={{ width: `${gifProgress}%` }} /></div>
              <small>{gifStatus || 'جاري تجهيز الملف...'}</small>
            </div>
          )}

          <small className="gif-export-note">الأرشيف يدوي بالكامل: كل جولة تُحفظ فقط إذا وافق الأدمن عليها بعد انتهاء عرضها. زر التحميل يحفظ GIF على جهازك فقط ولا يضيفه إلى الأرشيف.</small>
        </div>
      )}

      {self.isChef && !album.chainFinished && !album.finished && (
        <div className="album-controls">
          <button className="secondary-btn" disabled={transitionPending} onClick={() => control('chef:albumPrevious')}>السابق</button>
          <button className="primary-btn" disabled={transitionPending} onClick={() => control('chef:albumToggle')}>{album.playing ? 'إيقاف مؤقت ❚❚' : 'تشغيل ▶'}</button>
          <button className="secondary-btn" disabled={transitionPending} onClick={() => control('chef:albumNext')}>التالي</button>
          <span>{room.settings.albumDisplay === 'auto' ? 'عرض تلقائي متزامن لهذه الجولة' : 'عرض يدوي بواسطة الأدمن لهذه الجولة'}</span>
        </div>
      )}

      {!self.isChef && !album.chainFinished && !album.finished && <div className="viewer-sync-note"><i /> العرض يتحكم به الأدمن ويظهر للجميع بشكل متزامن</div>}
      {!self.isChef && album.chainFinished && !album.finished && <div className="viewer-sync-note"><i /> انتهت هذه الجولة. يمكنك تنزيل GIF الآن، وانتظر الأدمن لعرض الجولة التالية.</div>}
    </section>
  );
}

function useSynchronizedAlbum(album: RoomState['album'], sessionId: string | undefined, serverClockOffsetMs: number) {
  const [displayAlbum, setDisplayAlbum] = useState<NonNullable<RoomState['album']> | null>(null);
  const acceptedRevisionRef = useRef(-1);

  useEffect(() => {
    acceptedRevisionRef.current = -1;
    setDisplayAlbum(null);
  }, [sessionId]);

  useEffect(() => {
    if (!album) {
      acceptedRevisionRef.current = -1;
      setDisplayAlbum(null);
      return;
    }
    if (album.revision < acceptedRevisionRef.current) return;
    acceptedRevisionRef.current = album.revision;

    if (album.preparing || album.chainFinished || album.finished || !album.showAt) {
      setDisplayAlbum(roomAlbumClone(album));
      return;
    }

    const serverNow = Date.now() + serverClockOffsetMs;
    const delay = Math.max(0, album.showAt - serverNow);
    if (delay <= 24) {
      setDisplayAlbum(roomAlbumClone(album));
      return;
    }

    const timer = window.setTimeout(() => setDisplayAlbum(roomAlbumClone(album)), delay);
    return () => window.clearTimeout(timer);
  }, [
    album?.revision,
    album?.chainIndex,
    album?.entryIndex,
    album?.preparing,
    album?.chainFinished,
    album?.finished,
    album?.playing,
    album?.showAt,
    album?.stepDurationMs,
    serverClockOffsetMs
  ]);

  // Never leave a slow device showing an expired image. In automatic mode the
  // next state is normally pre-announced 1.2s early. If that tiny state packet
  // is delayed or dropped, blank the expired step and show the syncing screen
  // until the watchdog obtains the latest revision from the server.
  useEffect(() => {
    if (
      !displayAlbum ||
      !displayAlbum.playing ||
      displayAlbum.preparing ||
      displayAlbum.chainFinished ||
      displayAlbum.finished ||
      !displayAlbum.showAt ||
      !displayAlbum.stepDurationMs
    ) return;

    const displayedRevision = displayAlbum.revision;
    const serverNow = Date.now() + serverClockOffsetMs;
    const expiresAt = displayAlbum.showAt + displayAlbum.stepDurationMs + 120;
    const delay = Math.max(0, expiresAt - serverNow);
    const timer = window.setTimeout(() => {
      if (acceptedRevisionRef.current > displayedRevision) return;
      setDisplayAlbum((current) => current?.revision === displayedRevision ? null : current);
    }, delay);

    return () => window.clearTimeout(timer);
  }, [
    displayAlbum?.revision,
    displayAlbum?.playing,
    displayAlbum?.preparing,
    displayAlbum?.chainFinished,
    displayAlbum?.finished,
    displayAlbum?.showAt,
    displayAlbum?.stepDurationMs,
    serverClockOffsetMs
  ]);

  return displayAlbum;
}

function roomAlbumClone(album: RoomState['album']) {
  return album ? { ...album } : null;
}

async function preloadSketchChain(chain: GameChain): Promise<void> {
  const drawings = chain.entries.filter((entry) => entry.kind === 'drawing' && entry.value).map((entry) => entry.value);
  if (!drawings.length) return;
  await Promise.all(drawings.map((src) => preloadImage(src, 10_000)));
}

function preloadImage(src: string, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const image = new Image();
    let settled = false;
    const timer = window.setTimeout(done, timeoutMs);

    function done() {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      image.onload = null;
      image.onerror = null;
      resolve();
    }

    image.decoding = 'async';
    image.onload = () => {
      if (typeof image.decode === 'function') {
        void image.decode().catch(() => undefined).finally(done);
      } else {
        done();
      }
    };
    image.onerror = done;
    image.src = src;

    if (image.complete && image.naturalWidth > 0) {
      if (typeof image.decode === 'function') {
        void image.decode().catch(() => undefined).finally(done);
      } else {
        done();
      }
    }
  });
}

function TimeSetting({ label, value, min, max, onChange }: { label: string; value: number; min: number; max: number; onChange: (value: number) => void }) {
  return <label className="time-setting"><span><strong>{label}</strong><small>{value} ثانية</small></span><input type="range" min={min} max={max} step={10} value={value} onChange={(e) => onChange(Number(e.target.value))} /></label>;
}

function SegmentedSetting({ label, value, options, onChange }: { label: string; value: string; options: [string, string][]; onChange: (value: string) => void }) {
  return (
    <div className="seg-setting"><span>{label}</span><div>{options.map(([option, text]) => <button key={option} type="button" className={value === option ? 'active' : ''} onClick={() => onChange(option)}>{text}</button>)}</div></div>
  );
}

function ToggleSetting({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return <label className="toggle-setting"><span>{label}</span><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /><i /></label>;
}

function Splash() {
  return <div className="splash"><span className="brand-mark big">S</span><div className="spinner" /><p>جاري تجهيز اللعبة...</p></div>;
}

function AmbientBackground() {
  return <div className="ambient" aria-hidden="true"><span /><span /><span /><span /></div>;
}


function FunnyAvatar({
  name,
  className,
  style,
  as = 'span'
}: {
  name: string;
  className?: string;
  style?: CSSProperties;
  as?: 'span' | 'div' | 'i';
}) {
  const avatarStyle: CSSProperties = {
    ...(style || {}),
    backgroundImage: `url("${funnyAvatarDataUrl(name)}")`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    color: 'transparent',
    fontSize: 0,
    overflow: 'hidden'
  };

  const commonProps = {
    className,
    style: avatarStyle,
    'aria-label': `صورة ${name}`,
    title: name
  };

  if (as === 'div') return <div {...commonProps} />;
  if (as === 'i') return <i {...commonProps} />;
  return <span {...commonProps} />;
}

const funnyAvatarCache = new Map<string, string>();

function funnyAvatarDataUrl(name: string) {
  const safeName = name.trim() || '?';
  const cached = funnyAvatarCache.get(safeName);
  if (cached) return cached;

  const first = firstUsefulChar(safeName);
  const seed = hashString(safeName);

  const bgGradients = [
    ['#7c3aed', '#22d3ee'],
    ['#ec4899', '#8b5cf6'],
    ['#f59e0b', '#ef4444'],
    ['#10b981', '#3b82f6'],
    ['#f43f5e', '#06b6d4'],
    ['#84cc16', '#8b5cf6'],
    ['#14b8a6', '#f97316'],
    ['#6366f1', '#e879f9']
  ];

  const skins = ['#fde6cc', '#f6d2b4', '#f1c49f', '#eab38f', '#d99873'];
  const hairs = ['#1f2937', '#2b1d12', '#4c1d95', '#0f172a', '#7c2d12', '#3f3f46'];
  const shirtA = ['#ffffff', '#dbeafe', '#ede9fe', '#dcfce7', '#fee2e2', '#fef3c7'];
  const shirtB = ['#312e81', '#1d4ed8', '#7c3aed', '#0f766e', '#be123c', '#92400e'];
  const blushes = ['#f9a8d4', '#fda4af', '#fca5a5', '#fdba74'];
  const mouths = [
    'M44 77 Q60 89 76 77',
    'M44 80 Q60 72 76 80',
    'M46 78 Q60 92 74 78',
    'M46 76 Q60 82 74 76'
  ];
  const eyebrowModes = ['raise', 'flat', 'tilt', 'surprise'];
  const hairModes = ['spiky', 'messy', 'puff', 'punk', 'curly'];
  const extras = ['glasses', 'mustache', 'freckles', 'tooth', 'wink', 'none'];

  const gradient = bgGradients[seed % bgGradients.length];
  const skin = skins[(seed >> 1) % skins.length];
  const hair = hairs[(seed >> 2) % hairs.length];
  const shirtTop = shirtA[(seed >> 3) % shirtA.length];
  const shirtBottom = shirtB[(seed >> 4) % shirtB.length];
  const blush = blushes[(seed >> 5) % blushes.length];
  const mouth = mouths[(seed >> 6) % mouths.length];
  const eyebrowMode = eyebrowModes[(seed >> 7) % eyebrowModes.length];
  const hairMode = hairModes[(seed >> 8) % hairModes.length];
  const extra = extras[(seed >> 9) % extras.length];
  const prop = funnyPropForLetter(first, seed);
  const letter = escapeXml(displayLetter(first));

  const leftEye = extra === 'wink'
    ? '<path d="M41 57 Q47 54 53 57" stroke="#111827" stroke-width="3.4" stroke-linecap="round" fill="none" />'
    : '<circle cx="47" cy="58" r="4.5" fill="#111827" /><circle cx="48.5" cy="56.6" r="1.1" fill="#ffffff" />';

  const rightEye = '<circle cx="73" cy="58" r="4.5" fill="#111827" /><circle cx="74.5" cy="56.6" r="1.1" fill="#ffffff" />';

  const eyebrows = eyebrowSvg(eyebrowMode);
  const hairSvg = hairModeSvg(hairMode, hair);

  const extraSvg =
    extra === 'glasses' ? `
      <rect x="38" y="51" width="18" height="12" rx="5" fill="none" stroke="#111827" stroke-width="2.6" />
      <rect x="64" y="51" width="18" height="12" rx="5" fill="none" stroke="#111827" stroke-width="2.6" />
      <path d="M56 57 H64" stroke="#111827" stroke-width="2.4" stroke-linecap="round" />
    ` :
    extra === 'mustache' ? `
      <path d="M46 73 Q53 67 60 73 Q67 67 74 73" fill="none" stroke="#3f2a1d" stroke-width="4.4" stroke-linecap="round" />
    ` :
    extra === 'freckles' ? `
      <circle cx="39" cy="67" r="1.3" fill="#b45309" />
      <circle cx="43" cy="69" r="1.2" fill="#b45309" />
      <circle cx="80" cy="67" r="1.3" fill="#b45309" />
      <circle cx="76" cy="69" r="1.2" fill="#b45309" />
    ` :
    extra === 'tooth' ? `
      <rect x="57" y="77.4" width="6" height="5.4" rx="1.2" fill="#ffffff" />
    ` : '';

  const svg = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120">
  <defs>
    <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="${gradient[0]}" />
      <stop offset="100%" stop-color="${gradient[1]}" />
    </linearGradient>
    <clipPath id="clip">
      <circle cx="60" cy="60" r="56" />
    </clipPath>
  </defs>

  <circle cx="60" cy="60" r="56" fill="url(#g)" />
  <g clip-path="url(#clip)">
    <rect x="0" y="84" width="120" height="36" fill="${shirtTop}" />
    <path d="M15 120 Q31 85 60 92 Q89 85 105 120 Z" fill="${shirtBottom}" opacity="0.96" />

    <circle cx="60" cy="57" r="31" fill="${skin}" />
    <ellipse cx="44" cy="67" rx="5.8" ry="3.2" fill="${blush}" opacity="0.6" />
    <ellipse cx="77" cy="67" rx="5.8" ry="3.2" fill="${blush}" opacity="0.6" />

    ${hairSvg}
    ${eyebrows}
    ${leftEye}
    ${rightEye}

    <path d="M59 61 Q60 68 56 72" fill="none" stroke="#b45309" stroke-width="2" stroke-linecap="round" />
    <path d="${mouth}" fill="none" stroke="#111827" stroke-width="3.5" stroke-linecap="round" />
    ${extraSvg}

    <circle cx="92" cy="26" r="14" fill="rgba(255,255,255,.88)" />
    <text x="92" y="31" text-anchor="middle" font-size="14" font-weight="900">${escapeXml(prop)}</text>

    <circle cx="24" cy="25" r="13" fill="rgba(17,24,39,.28)" />
    <text x="24" y="30" text-anchor="middle" font-size="16" font-weight="900" fill="#ffffff">${letter}</text>
  </g>

  <circle cx="60" cy="60" r="56" fill="none" stroke="rgba(255,255,255,.36)" stroke-width="2" />
</svg>`.trim();

  const url = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  funnyAvatarCache.set(safeName, url);
  return url;
}

function displayLetter(char: string) {
  return /[a-z]/i.test(char) ? char.toUpperCase() : char;
}

function firstUsefulChar(name: string) {
  const chars = Array.from(name.trim());
  for (const char of chars) {
    if (/\p{L}|\p{N}/u.test(char)) return char;
  }
  return '?';
}

function hashString(value: string) {
  let hash = 0;
  for (const char of Array.from(value)) {
    hash = ((hash << 5) - hash) + char.codePointAt(0)!;
    hash |= 0;
  }
  return Math.abs(hash);
}

function eyebrowSvg(mode: string) {
  if (mode === 'raise') {
    return `
      <path d="M38 46 Q47 41 55 46" stroke="#111827" stroke-width="3.3" stroke-linecap="round" fill="none" />
      <path d="M65 46 Q74 41 82 46" stroke="#111827" stroke-width="3.3" stroke-linecap="round" fill="none" />
    `;
  }
  if (mode === 'tilt') {
    return `
      <path d="M39 48 Q47 42 54 45" stroke="#111827" stroke-width="3.3" stroke-linecap="round" fill="none" />
      <path d="M66 45 Q73 42 81 48" stroke="#111827" stroke-width="3.3" stroke-linecap="round" fill="none" />
    `;
  }
  if (mode === 'surprise') {
    return `
      <path d="M39 44 Q47 39 55 44" stroke="#111827" stroke-width="3.1" stroke-linecap="round" fill="none" />
      <path d="M65 44 Q73 39 81 44" stroke="#111827" stroke-width="3.1" stroke-linecap="round" fill="none" />
    `;
  }
  return `
    <path d="M39 47 Q47 45 55 47" stroke="#111827" stroke-width="3.3" stroke-linecap="round" fill="none" />
    <path d="M65 47 Q73 45 81 47" stroke="#111827" stroke-width="3.3" stroke-linecap="round" fill="none" />
  `;
}

function hairModeSvg(mode: string, color: string) {
  if (mode === 'messy') {
    return `
      <path d="M28 41 Q35 17 57 18 Q79 14 90 35 Q80 30 76 36 Q66 18 51 30 Q42 16 28 41 Z" fill="${color}" />
      <path d="M30 30 L24 18 L36 24 L40 12 L48 24 L56 14 L63 25 L73 12 L78 27 L92 22 L82 37" fill="none" stroke="${color}" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" />
    `;
  }
  if (mode === 'puff') {
    return `
      <circle cx="42" cy="31" r="10" fill="${color}" />
      <circle cx="55" cy="24" r="12" fill="${color}" />
      <circle cx="70" cy="27" r="11" fill="${color}" />
      <circle cx="81" cy="35" r="8" fill="${color}" />
      <path d="M31 44 Q37 26 58 26 Q80 24 88 42 L88 50 Q76 41 63 41 Q44 37 32 47 Z" fill="${color}" />
    `;
  }
  if (mode === 'punk') {
    return `
      <path d="M33 43 Q38 17 59 18 Q79 16 87 39 Q79 36 74 40 Q66 28 58 29 Q48 29 42 38 Q37 35 33 43 Z" fill="${color}" />
      <path d="M38 32 L43 14 L49 32 L56 8 L62 31 L70 13 L73 34" fill="none" stroke="${color}" stroke-width="4.5" stroke-linecap="round" />
    `;
  }
  if (mode === 'curly') {
    return `
      <path d="M31 43 Q35 20 58 20 Q80 18 89 41 Q79 37 74 41 Q65 25 53 30 Q43 20 31 43 Z" fill="${color}" />
      <circle cx="38" cy="31" r="5" fill="${color}" />
      <circle cx="48" cy="24" r="6" fill="${color}" />
      <circle cx="60" cy="22" r="6" fill="${color}" />
      <circle cx="72" cy="25" r="6" fill="${color}" />
      <circle cx="82" cy="33" r="5" fill="${color}" />
    `;
  }
  return `
    <path d="M30 44 Q33 19 58 19 Q83 16 90 41 Q83 37 76 40 Q69 27 59 31 Q47 19 30 44 Z" fill="${color}" />
    <path d="M36 31 L31 21 L42 25 L45 16 L53 24 L59 15 L66 26 L75 17 L79 29 L87 24" fill="none" stroke="${color}" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round" />
  `;
}

function funnyPropForLetter(letter: string, seed: number) {
  const latin = displayLetter(letter).toUpperCase();
  const arabicMap: Record<string, string[]> = {
    'ا': ['🍦', '🎩', '😎'],
    'أ': ['🍦', '🎩', '😎'],
    'إ': ['🍦', '🎩', '😎'],
    'آ': ['🍦', '🎩', '😎'],
    'ب': ['🎈', '🍔', '🪄'],
    'ت': ['👑', '🍎', '🧃'],
    'ث': ['🦊', '🎭', '⚡'],
    'ج': ['🥕', '🤹', '🎸'],
    'ح': ['🍉', '🧠', '🕶️'],
    'خ': ['🦸', '🍑', '🎤'],
    'د': ['🍩', '🥁', '🚀'],
    'ذ': ['🌽', '🎯', '🧲'],
    'ر': ['🤖', '🌶️', '🎀'],
    'ز': ['🦓', '⚽', '🎉'],
    'س': ['🥪', '🛹', '🎧'],
    'ش': ['🍫', '🎬', '🦄'],
    'ص': ['🐥', '📸', '🎨'],
    'ض': ['🐸', '🎲', '🧤'],
    'ط': ['🍅', '🎪', '🛴'],
    'ظ': ['🦁', '🪁', '🎷'],
    'ع': ['🍇', '🦉', '🎓'],
    'غ': ['🍄', '🎮', '🧁'],
    'ف': ['🍟', '🎻', '🕺'],
    'ق': ['☕', '🎯', '😹'],
    'ك': ['🥝', '🎹', '🧢'],
    'ل': ['🍋', '💡', '🎈'],
    'م': ['🍌', '🎤', '🪩'],
    'ن': ['🥜', '🎨', '🚲'],
    'ه': ['🌭', '🎃', '🤠'],
    'و': ['🥨', '🌊', '🛸'],
    'ي': ['🍦', '💎', '🎵']
  };

  const latinMap: Record<string, string[]> = {
    'A': ['🍎', '🛸', '🎨'],
    'B': ['🍔', '🎈', '🐝'],
    'C': ['🍪', '👑', '🎪'],
    'D': ['🍩', '🐶', '🥁'],
    'E': ['🥚', '👓', '⚡'],
    'F': ['🍟', '🦊', '🎣'],
    'G': ['🍇', '👻', '🎸'],
    'H': ['🍔', '🤠', '🎧'],
    'I': ['🍦', '🧊', '👁️'],
    'J': ['🫙', '🤹', '🧃'],
    'K': ['🥝', '🔑', '🪁'],
    'L': ['🍋', '🦁', '💡'],
    'M': ['🍄', '🎤', '🐵'],
    'N': ['🥜', '👃', '📘'],
    'O': ['🍊', '🐙', '🦉'],
    'P': ['🍕', '🐼', '🎈'],
    'Q': ['👸', '❓', '🍳'],
    'R': ['🤖', '🌈', '🚀'],
    'S': ['🥪', '⭐', '🐍'],
    'T': ['🌮', '🐯', '🎩'],
    'U': ['☂️', '🦄', '🛸'],
    'V': ['🎻', '🦇', '🎮'],
    'W': ['🧇', '🐺', '🌊'],
    'X': ['❌', '📦', '🧪'],
    'Y': ['🪀', '🛥️', '☀️'],
    'Z': ['🦓', '⚡', '😴']
  };

  const pool = arabicMap[letter] || latinMap[latin] || ['😹', '🎉', '🕶️'];
  return pool[seed % pool.length];
}

function escapeXml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}


function initials(name: string) {
  return name.trim().split(/\s+/).slice(0, 2).map((v) => v[0]?.toUpperCase()).join('');
}

function messageOf(error: unknown) {
  return error instanceof Error ? error.message : 'حدث خطأ غير متوقع.';
}

function emitAck(event: string, payload: unknown): Promise<AckResponse> {
  return new Promise((resolve, reject) => {
    let settled = false;
    let ackTimer: number | null = null;
    let connectTimer: number | null = null;

    const cleanup = () => {
      if (ackTimer !== null) window.clearTimeout(ackTimer);
      if (connectTimer !== null) window.clearTimeout(connectTimer);
      socket.off('connect', send);
    };
    const finish = (response: AckResponse) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(response);
    };
    const fail = (message: string) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error(message));
    };
    const send = () => {
      if (settled) return;
      socket.off('connect', send);
      if (connectTimer !== null) {
        window.clearTimeout(connectTimer);
        connectTimer = null;
      }
      ackTimer = window.setTimeout(() => fail('الاتصال بطيء جدًا ولم يصل تأكيد الخادم بعد. انتظر لحظات ثم حاول مجددًا.'), 30_000);
      socket.emit(event, payload, (response: AckResponse) => finish(response));
    };

    if (socket.connected) {
      send();
    } else {
      socket.on('connect', send);
      connectTimer = window.setTimeout(() => fail('جاري إعادة الاتصال بالخادم. انتظر حتى تظهر حالة «متصل» ثم حاول مرة أخرى.'), 12_000);
      socket.connect();
    }
  });
}
